import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Users, Building2, Film, DollarSign, Eye, Clock, TrendingUp, CheckCircle, XCircle, AlertCircle, BarChart3, Tv, ArrowUpRight, ArrowDownRight, Trash2, CreditCard, Settings, Wallet, ExternalLink, Link2, Megaphone, Play, MousePointer, SkipForward, Star, Search, GripVertical, X, User, Mail, Instagram, Facebook, Youtube, LinkIcon, ChevronDown, Layout, FileText, UserCheck, Banknote, Radio, Filter, Shield, ClipboardList, Flag, Download, Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { VideoPlayer } from '../components/VideoPlayer';
import LiveAudienceTab from '../components/LiveAudienceTab';
import LegalPrivacyPanel from '../components/LegalPrivacyPanel';
import AgreementsPanel from '../components/AgreementsPanel';
import { useAuth } from '../contexts/AuthContext';
import { ownerApi, analyticsApi, editRequestsApi, reportsApi, legalApi } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

// ── Reusable Filter Bar ──
const FilterBar = ({ filters, active, onChange, testIdPrefix }) => (
  <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid={`${testIdPrefix}-filters`}>
    <Filter className="w-3.5 h-3.5 text-gray-500 mr-1" />
    {filters.map((f) => (
      <button
        key={f.value}
        onClick={() => onChange(f.value)}
        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${active === f.value ? 'bg-[#8B5CF6]/20 text-[#8B5CF6] ring-1 ring-[#8B5CF6]/30' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
        data-testid={`${testIdPrefix}-filter-${f.value}`}
      >
        {f.label}{f.count !== undefined ? ` (${f.count})` : ''}
      </button>
    ))}
  </div>
);

const OwnerDashboardPage = () => {
  const { user, isAuthenticated, isOwner } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isOwnerRole = user?.role === 'owner';

  // URL-based state: read tab + filter from URL on mount
  const initialTab = searchParams.get('tab') || 'content-review';
  const initialFilter = searchParams.get('filter') || 'all';

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [activeFilter, setActiveFilter] = useState(initialFilter);
  const [stats, setStats] = useState(null);
  const [pendingCompanies, setPendingCompanies] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [pendingFilms, setPendingFilms] = useState([]);
  const [pendingSeries, setPendingSeries] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [revenueData, setRevenueData] = useState(null);
  
  // New states for content management
  const [allFilms, setAllFilms] = useState([]);
  const [allSeries, setAllSeries] = useState([]);
  const [allSubscriptions, setAllSubscriptions] = useState([]);
  const [seriesEpisodes, setSeriesEpisodes] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState(null);
  
  const [rejectDialog, setRejectDialog] = useState({ open: false, type: null, id: null });
  const [rejectReason, setRejectReason] = useState('');
  const [rejectNote, setRejectNote] = useState('');
  
  const [approveDialog, setApproveDialog] = useState({ open: false, type: null, id: null, name: '' });
  const [approveNote, setApproveNote] = useState('');
  
  const [companyAnalyticsDialog, setCompanyAnalyticsDialog] = useState({ open: false, company: null, analytics: null });
  
  // Delete confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null, name: '' });
  const [episodesDialog, setEpisodesDialog] = useState({ open: false, seriesId: null, seriesName: '' });

  // Edit content dialog
  const [editDialog, setEditDialog] = useState({ open: false, film: null });
  const [editForm, setEditForm] = useState({ title: '', description: '', genres: [], year: 2025, youtube_id: '', vimeo_id: '', content_rating: '' });
  const [editSaving, setEditSaving] = useState(false);

  // Content analytics dialog
  const [analyticsDialog, setAnalyticsDialog] = useState({ open: false, data: null, loading: false });

  // Ad management
  const [adConfig, setAdConfig] = useState({
    pre_roll_enabled: true,
    pre_roll_duration: 5,
    pre_roll_skip_after: 5,
    mid_roll_enabled: false,
    mid_roll_interval: 600,
    banner_enabled: true,
    overlay_enabled: false,
    adsense_publisher_id: '',
    adsense_enabled: false
  });
  const [adStats, setAdStats] = useState(null);
  const [savingAdConfig, setSavingAdConfig] = useState(false);

  // Creator payouts
  const [pendingPayouts, setPendingPayouts] = useState(null);
  const [processingPayouts, setProcessingPayouts] = useState(false);

  // Featured content management
  const [allContent, setAllContent] = useState([]);
  const [featuredIds, setFeaturedIds] = useState([]);
  const [featuredSearch, setFeaturedSearch] = useState('');
  const [featuredGenreFilter, setFeaturedGenreFilter] = useState('');
  const [savingFeatured, setSavingFeatured] = useState(false);

  // Admin management state
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminForm, setAdminForm] = useState({ email: '', name: '', password: '' });
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [deleteAdminDialog, setDeleteAdminDialog] = useState({ open: false, user: null });
  // Ban management state
  const [banDialog, setBanDialog] = useState({ open: false, user: null });
  const [policyHistoryDialog, setPolicyHistoryDialog] = useState({ open: false, userId: null, userName: '' });
  const [policyConsents, setPolicyConsents] = useState([]);
  const [banReason, setBanReason] = useState('');
  // Transaction history
  const [transactions, setTransactions] = useState([]);
  // Detailed analytics
  const [detailedAnalytics, setDetailedAnalytics] = useState(null);
  // Edit requests
  const [editRequests, setEditRequests] = useState([]);
  const [editRequestFilter, setEditRequestFilter] = useState('pending');
  const [editReqAction, setEditReqAction] = useState(null);
  const [editReqReason, setEditReqReason] = useState('');

  // Creator agreements
  const [agreementsDialog, setAgreementsDialog] = useState({ open: false, companyId: null, companyName: '' });
  const [agreementsData, setAgreementsData] = useState(null);
  const [agreementsLoading, setAgreementsLoading] = useState(false);

  // People search
  const [peopleSearch, setPeopleSearch] = useState('');

  // Financials date filter
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Revenue period filter
  const [revenuePeriod, setRevenuePeriod] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [pendingPeriod, setPendingPeriod] = useState(null);
  const [showPeriodConfirm, setShowPeriodConfirm] = useState(false);
  const [revenueLoading, setRevenueLoading] = useState(false);
  // ── URL ↔ State sync ──
  const changeTab = useCallback((tab) => {
    setActiveTab(tab);
    setActiveFilter('all');
    setPeopleSearch('');
    setDateFrom('');
    setDateTo('');
    setSearchParams({ tab, filter: 'all' }, { replace: true });
  }, [setSearchParams]);

  const changeFilter = useCallback((filter) => {
    setActiveFilter(filter);
    setSearchParams({ tab: activeTab, filter }, { replace: true });
  }, [activeTab, setSearchParams]);

  // Sync from URL on popstate (back/forward)
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    const urlFilter = searchParams.get('filter');
    if (urlTab && urlTab !== activeTab) setActiveTab(urlTab);
    if (urlFilter && urlFilter !== activeFilter) setActiveFilter(urlFilter);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (user?.role !== 'owner' && user?.role !== 'admin') { navigate('/browse'); toast.error('Access denied'); return; }
    fetchAllData();
  }, [isAuthenticated, user, navigate]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const basePromises = [
        analyticsApi.getPlatformStats(),
        ownerApi.getPendingCompanies(),
        ownerApi.getAllCompanies(),
        ownerApi.getPendingFilms().catch(() => []),
        ownerApi.getPendingSeries().catch(() => []),
        ownerApi.getAllUsers(),
        ownerApi.getAllFilms().catch(() => []),
        ownerApi.getAllSeries().catch(() => []),
        ownerApi.getAdConfig().catch(() => null),
        ownerApi.getAdStats().catch(() => null),
        ownerApi.getAllContent().catch(() => []),
        ownerApi.getFeaturedConfig().catch(() => ({ film_ids: [] })),
      ];

      const [statsData, pendingComp, allComp, pFilms, pSeries, users, films, series, adConfigData, adStatsData, contentData, featuredConfigData] = await Promise.all(basePromises);
      setStats(statsData);
      setPendingCompanies(pendingComp);
      setAllCompanies(allComp);
      setPendingFilms(pFilms);
      setPendingSeries(pSeries);
      setAllUsers(users);
      setAllFilms(films);
      setAllSeries(series);
      if (adConfigData) setAdConfig(adConfigData);
      setAdStats(adStatsData);
      setAllContent(contentData);
      setFeaturedIds(featuredConfigData?.film_ids || []);

      // Fetch edit requests
      try {
        const editReqData = await editRequestsApi.getAllRequests();
        setEditRequests(editReqData.requests || []);
      } catch (e) {
        console.error('Failed to fetch edit requests:', e);
      }

      // Owner-only data: financial
      if (isOwnerRole) {
        const [revenue, subs, payoutData, pendingPayoutsData, adminsData, txns] = await Promise.all([
          analyticsApi.getRevenueAnalytics().catch(() => null),
          ownerApi.getAllSubscriptions().catch(() => []),
          ownerApi.getPayoutSettings().catch(() => null),
          ownerApi.getPendingPayouts().catch(() => null),
          ownerApi.listAdmins().catch(() => []),
          ownerApi.getTransactions().catch(() => []),
        ]);
        setRevenueData(revenue);
        setAllSubscriptions(subs);
        setPendingPayouts(pendingPayoutsData);
        setAdminUsers(adminsData);
        setTransactions(txns);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveCompany = async () => {
    try { 
      await ownerApi.approveCompany(approveDialog.id, approveNote); 
      toast.success('Studio approved - notification email sent'); 
      setApproveDialog({ open: false, type: null, id: null, name: '' });
      setApproveNote('');
      fetchAllData(); 
    }
    catch (error) { toast.error(error.message || 'Failed to approve company'); }
  };

  const handleRejectCompany = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    try {
      await ownerApi.rejectCompany(rejectDialog.id, rejectReason, rejectNote);
      toast.success('Studio rejected - notification email sent');
      setRejectDialog({ open: false, type: null, id: null });
      setRejectReason('');
      setRejectNote('');
      fetchAllData();
    } catch (error) { toast.error(error.message || 'Failed to reject'); }
  };

  // Revenue period filter logic
  const getDateRange = (period) => {
    const now = new Date();
    const to = now.toISOString().split('T')[0];
    if (period === 'today') return { from: to, to };
    if (period === '1week') {
      const d = new Date(now); d.setDate(d.getDate() - 7);
      return { from: d.toISOString().split('T')[0], to };
    }
    if (period === '1month') {
      const d = new Date(now); d.setDate(d.getDate() - 30);
      return { from: d.toISOString().split('T')[0], to };
    }
    if (period === 'custom') return { from: customDateFrom, to: customDateTo };
    return { from: '2026-01-01', to }; // all time starts from 01/01/2026
  };

  const applyRevenuePeriod = async (period) => {
    setRevenuePeriod(period);
    setShowPeriodConfirm(false);
    setPendingPeriod(null);
    setRevenueLoading(true);
    try {
      const range = getDateRange(period);
      setDateFrom(range.from);
      setDateTo(range.to);
      const data = await analyticsApi.getRevenueAnalytics(range.from, range.to);
      setRevenueData(data);
    } catch (err) {
      toast.error('Failed to load revenue data');
    } finally {
      setRevenueLoading(false);
    }
  };

  const handlePeriodSelect = (period) => {
    if (period === 'custom' && (!customDateFrom || !customDateTo)) {
      setRevenuePeriod('custom');
      return;
    }
    applyRevenuePeriod(period);
  };

  const handleApproveContent = async () => {
    try { 
      await ownerApi.approveContent(approveDialog.id, approveNote); 
      toast.success('Content approved - notification email sent'); 
      setApproveDialog({ open: false, type: null, id: null, name: '' });
      setApproveNote('');
      fetchAllData(); 
    }
    catch (error) { toast.error(error.message || 'Failed to approve content'); }
  };

  const handleRejectContent = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    try {
      await ownerApi.rejectContent(rejectDialog.id, rejectReason, rejectNote);
      toast.success('Content rejected - notification email sent');
      setRejectDialog({ open: false, type: null, id: null });
      setRejectReason('');
      setRejectNote('');
      fetchAllData();
    } catch (error) { toast.error(error.message || 'Failed to reject'); }
  };

  const handleViewCompanyAnalytics = async (company) => {
    try {
      const analytics = await analyticsApi.getCompanyAnalytics(company.company_id);
      setCompanyAnalyticsDialog({ open: true, company, analytics });
    } catch (error) { toast.error('Failed to load company analytics'); }
  };

  const handleViewAgreements = async (company) => {
    setAgreementsDialog({ open: true, companyId: company.company_id, companyName: company.name });
    setAgreementsLoading(true);
    try {
      const data = await ownerApi.getCreatorAgreements(company.company_id);
      setAgreementsData(data);
    } catch (error) {
      toast.error('Failed to load agreements');
      setAgreementsData(null);
    } finally {
      setAgreementsLoading(false);
    }
  };

  const handleDownloadAgreements = (companyId) => {
    const url = ownerApi.downloadCreatorAgreements(companyId);
    window.open(url, '_blank');
  };

  // Delete handlers
  const handleDelete = async () => {
    const { type, id } = deleteDialog;
    try {
      switch (type) {
        case 'film':
          await ownerApi.deleteFilm(id);
          toast.success('Film deleted');
          break;
        case 'series':
          await ownerApi.deleteSeries(id);
          toast.success('Series and all episodes deleted');
          break;
        case 'episode':
          await ownerApi.deleteEpisode(id);
          toast.success('Episode deleted');
          // Refresh episodes if dialog is open
          if (selectedSeries) {
            const eps = await ownerApi.getSeriesEpisodes(selectedSeries);
            setSeriesEpisodes(eps);
          }
          break;
        case 'company':
          await ownerApi.deleteCompany(id);
          toast.success('Studio and all content deleted');
          break;
        case 'subscription':
          await ownerApi.deleteSubscription(id);
          toast.success('Subscription deleted');
          break;
        default:
          break;
      }
      setDeleteDialog({ open: false, type: null, id: null, name: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.message || `Failed to delete ${type}`);
    }
  };

  const handleViewEpisodes = async (series) => {
    try {
      const eps = await ownerApi.getSeriesEpisodes(series.film_id);
      setSeriesEpisodes(eps);
      setSelectedSeries(series.film_id);
      setEpisodesDialog({ open: true, seriesId: series.film_id, seriesName: series.title });
    } catch (error) {
      toast.error('Failed to load episodes');
    }
  };

  // Ad config handler
  const handleSaveAdConfig = async () => {
    setSavingAdConfig(true);
    try {
      await ownerApi.updateAdConfig(adConfig);
      toast.success('Ad configuration saved successfully');
      fetchAllData();
    } catch (error) {
      toast.error(error.message || 'Failed to save ad configuration');
    } finally {
      setSavingAdConfig(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: 'bg-[#00FF9D]/20 text-[#00FF9D] border-[#00FF9D]/30',
      active: 'bg-[#00FF9D]/20 text-[#00FF9D] border-[#00FF9D]/30',
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
      paused: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      suspended: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      expired: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return <span className={`badge text-xs ${styles[status] || ''}`}>{status}</span>;
  };

  const GENRE_OPTIONS = [
    'Action', 'Adventure', 'Animation', 'Biography', 'Comedy', 'Crime',
    'Documentary', 'Drama', 'Family', 'Fantasy', 'Historical', 'Horror',
    'Musical', 'Mystery', 'Romance', 'Sci-Fi', 'Sports', 'Thriller',
    'War', 'Western',
  ];

  const handleOpenEdit = (film) => {
    setEditForm({
      title: film.title || '',
      description: film.description || '',
      genres: film.genres || [],
      year: film.year || 2025,
      youtube_id: film.youtube_id || '',
      vimeo_id: film.vimeo_id || '',
      content_rating: film.content_rating || '',
    });
    setEditDialog({ open: true, film });
  };

  const handleSaveEdit = async () => {
    setEditSaving(true);
    try {
      await ownerApi.updateContent(editDialog.film.film_id, editForm);
      toast.success('Content updated');
      setEditDialog({ open: false, film: null });
      fetchAllData();
    } catch (err) {
      toast.error(err.message || 'Update failed');
    } finally { setEditSaving(false); }
  };

  const toggleEditGenre = (genre) => {
    setEditForm(prev => ({
      ...prev,
      genres: prev.genres.includes(genre)
        ? prev.genres.filter(g => g !== genre)
        : prev.genres.length < 5 ? [...prev.genres, genre] : prev.genres,
    }));
  };

  const handleViewAnalytics = async (filmId) => {
    setAnalyticsDialog({ open: true, data: null, loading: true });
    setDetailedAnalytics(null);
    try {
      const data = await ownerApi.getDetailedAnalytics(filmId);
      setDetailedAnalytics(data);
      setAnalyticsDialog({ open: true, data, loading: false });
    } catch (err) {
      toast.error('Failed to load analytics');
      setAnalyticsDialog({ open: false, data: null, loading: false });
    }
  };

  const openPolicyHistory = async (userId, userName) => {
    setPolicyHistoryDialog({ open: true, userId, userName });
    try {
      const data = await legalApi.getUserPolicyConsents(userId);
      setPolicyConsents(data.consents || []);
    } catch { setPolicyConsents([]); }
  };

  const handleBanUser = async () => {
    if (!banDialog.user) return;
    try {
      await ownerApi.banUser(banDialog.user.user_id, banReason || 'Violation of terms');
      toast.success(`${banDialog.user.name} has been banned`);
      setBanDialog({ open: false, user: null });
      setBanReason('');
      fetchAllData();
    } catch (err) {
      toast.error(err.message || 'Failed to ban user');
    }
  };

  // Admin management handlers
  const handleCreateAdmin = async () => {
    if (!adminForm.email || !adminForm.name || !adminForm.password) {
      toast.error('All fields are required');
      return;
    }
    setCreatingAdmin(true);
    try {
      await ownerApi.createAdmin(adminForm);
      toast.success('Admin created successfully');
      setAdminForm({ email: '', name: '', password: '' });
      const admins = await ownerApi.listAdmins();
      setAdminUsers(admins);
    } catch (err) {
      toast.error(err.message || 'Failed to create admin');
    } finally {
      setCreatingAdmin(false);
    }
  };

  const handleDeleteAdmin = async () => {
    if (!deleteAdminDialog.user) return;
    try {
      await ownerApi.deleteAdmin(deleteAdminDialog.user.user_id);
      toast.success('Admin deleted');
      setDeleteAdminDialog({ open: false, user: null });
      const admins = await ownerApi.listAdmins();
      setAdminUsers(admins);
    } catch (err) {
      toast.error(err.message || 'Failed to delete admin');
    }
  };

  // Content preview dialog
  const [previewDialog, setPreviewDialog] = useState({ open: false, item: null, episodes: null, selectedEp: null });

  const handlePreviewContent = async (item) => {
    if (item.content_type === 'series') {
      try {
        const eps = await ownerApi.getSeriesEpisodes(item.film_id);
        const epList = eps || [];
        setPreviewDialog({ open: true, item, episodes: epList, selectedEp: epList.length > 0 ? epList[0] : null });
      } catch (e) {
        setPreviewDialog({ open: true, item, episodes: [], selectedEp: null });
      }
    } else {
      setPreviewDialog({ open: true, item, episodes: null, selectedEp: null });
    }
  };

  const getPreviewVideoData = () => {
    const p = previewDialog;
    if (!p.item) return null;
    if (p.item.content_type === 'series' && p.selectedEp) {
      return {
        ...p.item,
        video_file: p.selectedEp.video_file,
        title: `${p.item.title} - S${p.selectedEp.season_number}E${p.selectedEp.episode_number}: ${p.selectedEp.title}`,
      };
    }
    return p.item;
  };

  const ContentReviewCard = ({ item, type = 'film' }) => (
    <div key={item.film_id} className="admin-card p-4 flex gap-4" data-testid={`pending-${type}-${item.film_id}`}>
      <img src={item.thumbnail} alt={item.title} className="w-48 h-28 object-cover rounded" />
      <div className="flex-1">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold">{item.title}</h3>
              {item.content_type === 'series' && (
                <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-[#00FF9D]/20 text-[#00FF9D] flex items-center gap-1">
                  <Tv className="w-3 h-3" /> Series
                </span>
              )}
              {item.status === 'paused' && (
                <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-orange-500/20 text-orange-400">PAUSED</span>
              )}
              {item.pending_version && (
                <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-blue-500/20 text-blue-400">EDIT REQUEST</span>
              )}
            </div>
            <p className="text-sm text-gray-400">{item.creator}</p>
            {item.company && <p className="text-xs text-[#8B5CF6]">Creator: {item.company.name}</p>}
            <p className="text-sm text-gray-300 mt-2 line-clamp-2">{item.description}</p>
            {item.paused_reason && item.status === 'paused' && (
              <p className="text-xs text-orange-400 mt-1">Paused reason: {item.paused_reason}</p>
            )}
            {item.episode_count !== undefined && (
              <p className="text-xs text-gray-500 mt-1">{item.episode_count} episodes</p>
            )}
            <div className="flex gap-1.5 mt-2 flex-wrap">
              {item.genres?.slice(0, 3).map((g) => (
                <span key={g} className="px-2 py-0.5 text-[10px] rounded-sm bg-[#8B5CF6]/20 text-[#8B5CF6]">{g}</span>
              ))}
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button onClick={() => handlePreviewContent(item)} size="sm" variant="outline" className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10" data-testid={`preview-content-${item.film_id}`}>
              <Play className="w-4 h-4 mr-1" /> Preview
            </Button>
            <Button onClick={() => setApproveDialog({ open: true, type: 'content', id: item.film_id, name: item.title })} size="sm" className="bg-[#00FF9D]/20 text-[#00FF9D] hover:bg-[#00FF9D]/30" data-testid={`approve-content-${item.film_id}`}>
              <CheckCircle className="w-4 h-4" />
            </Button>
            <Button onClick={() => setRejectDialog({ open: true, type: 'content', id: item.film_id })} size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`reject-content-${item.film_id}`}>
              <XCircle className="w-4 h-4" />
            </Button>
            <Button onClick={() => setDeleteDialog({ open: true, type: 'film', id: item.film_id, name: item.title })} size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`delete-content-${item.film_id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Filtered data (must be before any conditional returns) ──
  const pendingEditRequests = editRequests.filter(r => r.status === 'pending');
  const totalPending = pendingFilms.length + pendingSeries.length + pendingEditRequests.length;

  const filteredReviewFilms = useMemo(() => {
    if (activeFilter === 'series') return [];
    return pendingFilms;
  }, [pendingFilms, activeFilter]);

  const filteredReviewSeries = useMemo(() => {
    if (activeFilter === 'films') return [];
    return pendingSeries;
  }, [pendingSeries, activeFilter]);

  const filteredPublishedFilms = useMemo(() => {
    if (activeFilter === 'series') return [];
    return allFilms;
  }, [allFilms, activeFilter]);

  const filteredPublishedSeries = useMemo(() => {
    if (activeFilter === 'films') return [];
    return allSeries;
  }, [allSeries, activeFilter]);

  const filteredCreators = useMemo(() => {
    const combined = [...pendingCompanies.map(c => ({...c, _isPending: true})), ...allCompanies];
    const seen = new Set();
    let deduped = combined.filter(c => { if (seen.has(c.company_id)) return false; seen.add(c.company_id); return true; });
    if (activeFilter === 'pending') deduped = deduped.filter(c => c.status === 'pending' || c._isPending);
    else if (activeFilter === 'approved') deduped = deduped.filter(c => c.status === 'approved');
    else if (activeFilter === 'rejected') deduped = deduped.filter(c => c.status === 'rejected');
    if (peopleSearch.trim()) {
      const q = peopleSearch.toLowerCase();
      deduped = deduped.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.first_name || '').toLowerCase().includes(q) ||
        (c.last_name || '').toLowerCase().includes(q) ||
        (c.contact_email || '').toLowerCase().includes(q)
      );
    }
    return deduped;
  }, [allCompanies, pendingCompanies, activeFilter, peopleSearch]);

  const filteredUsers = useMemo(() => {
    let list = allUsers;
    if (activeFilter === 'active') list = list.filter(u => !u.is_banned);
    else if (activeFilter === 'banned') list = list.filter(u => u.is_banned);
    else if (activeFilter === 'user') list = list.filter(u => u.role === 'user');
    else if (activeFilter === 'creator') list = list.filter(u => u.role === 'company');
    else if (activeFilter === 'admin') list = list.filter(u => u.role === 'admin' || u.role === 'owner');
    else if (activeFilter === 'premium') list = list.filter(u => u.subscription);
    if (peopleSearch.trim()) {
      const q = peopleSearch.toLowerCase();
      list = list.filter(u =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [allUsers, activeFilter, peopleSearch]);

  const filteredSubscriptions = useMemo(() => {
    let list = allSubscriptions;
    if (activeFilter !== 'all') list = list.filter(s => s.status === activeFilter);
    if (dateFrom) list = list.filter(s => (s.created_at || s.started_at || '') >= dateFrom);
    if (dateTo) list = list.filter(s => (s.created_at || s.started_at || '') <= dateTo + 'T23:59:59');
    return list;
  }, [allSubscriptions, activeFilter, dateFrom, dateTo]);

  const filteredTransactions = useMemo(() => {
    let list = transactions;
    if (activeFilter === 'subscription' || activeFilter === 'donation' || activeFilter === 'payout' || activeFilter === 'premium_content' || activeFilter === 'merchandise') {
      list = list.filter(t => t.type === activeFilter);
    } else if (activeFilter === 'completed') {
      list = list.filter(t => t.status === 'paid' || t.status === 'completed');
    } else if (activeFilter === 'pending') {
      list = list.filter(t => t.status === 'pending');
    } else if (activeFilter === 'failed') {
      list = list.filter(t => t.status === 'failed');
    }
    if (dateFrom) list = list.filter(t => (t.created_at || t.date || '') >= dateFrom);
    if (dateTo) list = list.filter(t => (t.created_at || t.date || '') <= dateTo + 'T23:59:59');
    return list;
  }, [transactions, activeFilter, dateFrom, dateTo]);

  const reviewFilters = [
    { value: 'all', label: 'All', count: pendingFilms.length + pendingSeries.length },
    { value: 'films', label: 'Movies', count: pendingFilms.length },
    { value: 'series', label: 'Series', count: pendingSeries.length },
  ];
  const publishedFilters = [
    { value: 'all', label: 'All', count: allFilms.length + allSeries.length },
    { value: 'films', label: 'Movies', count: allFilms.length },
    { value: 'series', label: 'Series', count: allSeries.length },
  ];
  const creatorFilters = [
    { value: 'all', label: 'All' },
    { value: 'approved', label: 'Approved', count: allCompanies.filter(c => c.status === 'approved').length },
    { value: 'pending', label: 'Pending', count: pendingCompanies.length },
    { value: 'rejected', label: 'Rejected', count: allCompanies.filter(c => c.status === 'rejected').length },
  ];
  const userFilters = [
    { value: 'all', label: 'All', count: allUsers.length },
    { value: 'active', label: 'Active', count: allUsers.filter(u => !u.is_banned).length },
    { value: 'banned', label: 'Banned', count: allUsers.filter(u => u.is_banned).length },
    { value: 'user', label: 'Users', count: allUsers.filter(u => u.role === 'user').length },
    { value: 'creator', label: 'Creators', count: allUsers.filter(u => u.role === 'company').length },
    { value: 'premium', label: 'Premium', count: allUsers.filter(u => u.subscription).length },
  ];
  const subFilters = [
    { value: 'all', label: 'All', count: allSubscriptions.length },
    { value: 'active', label: 'Active', count: allSubscriptions.filter(s => s.status === 'active').length },
    { value: 'cancelled', label: 'Cancelled', count: allSubscriptions.filter(s => s.status === 'cancelled').length },
    { value: 'expired', label: 'Expired', count: allSubscriptions.filter(s => s.status === 'expired').length },
  ];
  const txFilters = [
    { value: 'all', label: 'All', count: transactions.length },
    { value: 'subscription', label: 'Subscriptions', count: transactions.filter(t => t.type === 'subscription').length },
    { value: 'premium_content', label: 'PPV', count: transactions.filter(t => t.type === 'premium_content').length },
    { value: 'donation', label: 'Donations', count: transactions.filter(t => t.type === 'donation').length },
    { value: 'merchandise', label: 'Merchandise', count: transactions.filter(t => t.type === 'merchandise').length },
    { value: 'payout', label: 'Payouts', count: transactions.filter(t => t.type === 'payout').length },
    { value: 'completed', label: 'Completed', count: transactions.filter(t => t.status === 'paid' || t.status === 'completed').length },
    { value: 'pending', label: 'Pending', count: transactions.filter(t => t.status === 'pending').length },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030014] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030014]" data-testid="owner-dashboard-page">
      <Navbar />

      <div className="pt-24 pb-12 max-w-[1600px] mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tighter" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {isOwnerRole ? 'Owner Dashboard' : 'Admin Dashboard'}
          </h1>
          <p className="text-gray-400">Platform management and analytics</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className={`grid grid-cols-2 md:grid-cols-4 ${isOwnerRole ? 'lg:grid-cols-6' : 'lg:grid-cols-5'} gap-4 mb-8`}>
            <div className="admin-card p-4">
              <Users className="w-6 h-6 text-[#8B5CF6] mb-2" />
              <p className="text-2xl font-bold">{stats.total_users}</p>
              <p className="text-xs text-gray-400">Total Users</p>
            </div>
            <div className="admin-card p-4">
              <TrendingUp className="w-6 h-6 text-[#00FF9D] mb-2" />
              <p className="text-2xl font-bold">{stats.total_subscribers}</p>
              <p className="text-xs text-gray-400">Subscribers</p>
            </div>
            <div className="admin-card p-4">
              <Building2 className="w-6 h-6 text-blue-400 mb-2" />
              <p className="text-2xl font-bold">{stats.total_companies}</p>
              <p className="text-xs text-gray-400">Creators</p>
            </div>
            <div className="admin-card p-4">
              <Film className="w-6 h-6 text-pink-400 mb-2" />
              <p className="text-2xl font-bold">{stats.total_content}</p>
              <p className="text-xs text-gray-400">Content</p>
            </div>
            <div className="admin-card p-4">
              <Eye className="w-6 h-6 text-orange-400 mb-2" />
              <p className="text-2xl font-bold">{stats.total_views?.toLocaleString()}</p>
              <p className="text-xs text-gray-400">Total Views</p>
            </div>
            {isOwnerRole && (
              <div className="admin-card p-4">
                <DollarSign className="w-6 h-6 text-green-400 mb-2" />
                <p className="text-2xl font-bold">${stats.total_revenue}</p>
                <p className="text-xs text-gray-400">Revenue</p>
              </div>
            )}
          </div>
        )}

        {/* Pending Alert */}
        {(pendingCompanies.length > 0 || totalPending > 0) && (
          <div className="admin-card p-4 mb-8 border-yellow-500/30 bg-yellow-500/5">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-400" />
              <div>
                <p className="font-medium">Items Pending Review</p>
                <p className="text-sm text-gray-400">
                  {pendingCompanies.length} creators, {pendingFilms.length} films, {pendingSeries.length} series{pendingEditRequests.length > 0 ? `, ${pendingEditRequests.length} edit requests` : ''} awaiting approval
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={changeTab} className="space-y-6">
          {/* Grouped Dropdown Navigation */}
          <nav className="flex flex-wrap items-center gap-2 p-2 bg-[#0F0B1E] border border-[#8B5CF6]/20 rounded-lg" data-testid="dashboard-nav">
            {/* Content Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${['content-review','content-published','featured','edit-requests'].includes(activeTab) ? 'bg-[#8B5CF6]/20 text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'}`} data-testid="nav-content">
                  <Film className="w-4 h-4" /> Content <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-[#1a1035] border-[#8B5CF6]/20">
                <DropdownMenuItem onClick={() => changeTab('content-review')} className="cursor-pointer" data-testid="nav-content-review">
                  <AlertCircle className="w-4 h-4 mr-2 text-yellow-400" /> To Review
                  {(pendingFilms.length + pendingSeries.length) > 0 && <span className="ml-auto text-xs bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded">{pendingFilms.length + pendingSeries.length}</span>}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeTab('edit-requests')} className="cursor-pointer" data-testid="nav-edit-requests">
                  <ClipboardList className="w-4 h-4 mr-2 text-orange-400" /> Edit Requests
                  {editRequests.filter(r => r.status === 'pending').length > 0 && <span className="ml-auto text-xs bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded">{editRequests.filter(r => r.status === 'pending').length}</span>}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeTab('content-published')} className="cursor-pointer" data-testid="nav-content-published">
                  <CheckCircle className="w-4 h-4 mr-2 text-[#00FF9D]" /> Published
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeTab('featured')} className="cursor-pointer" data-testid="nav-content-featured">
                  <Star className="w-4 h-4 mr-2 text-yellow-400" /> Featured
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* People Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${['people-creators','users','admins'].includes(activeTab) ? 'bg-[#8B5CF6]/20 text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'}`} data-testid="nav-people">
                  <Users className="w-4 h-4" /> People <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-[#1a1035] border-[#8B5CF6]/20">
                <DropdownMenuItem onClick={() => changeTab('people-creators')} className="cursor-pointer" data-testid="nav-people-creators">
                  <Building2 className="w-4 h-4 mr-2 text-[#8B5CF6]" /> Creators
                  {pendingCompanies.length > 0 && <span className="ml-auto text-xs bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded">{pendingCompanies.length}</span>}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeTab('users')} className="cursor-pointer" data-testid="nav-people-users">
                  <User className="w-4 h-4 mr-2 text-blue-400" /> Users
                </DropdownMenuItem>
                {isOwnerRole && (
                  <DropdownMenuItem onClick={() => changeTab('admins')} className="cursor-pointer" data-testid="nav-people-admins">
                    <UserCheck className="w-4 h-4 mr-2 text-[#00FF9D]" /> Admins
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Financials Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${['revenue','subscriptions','ads','transactions'].includes(activeTab) ? 'bg-[#8B5CF6]/20 text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'}`} data-testid="nav-financials">
                  <Banknote className="w-4 h-4" /> Financials <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-[#1a1035] border-[#8B5CF6]/20">
                {isOwnerRole && (
                  <DropdownMenuItem onClick={() => changeTab('revenue')} className="cursor-pointer" data-testid="nav-financials-revenue">
                    <DollarSign className="w-4 h-4 mr-2 text-[#00FF9D]" /> Revenue
                  </DropdownMenuItem>
                )}
                {isOwnerRole && (
                  <DropdownMenuItem onClick={() => changeTab('subscriptions')} className="cursor-pointer" data-testid="nav-financials-subscriptions">
                    <CreditCard className="w-4 h-4 mr-2 text-[#8B5CF6]" /> Subscriptions
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => changeTab('ads')} className="cursor-pointer" data-testid="nav-financials-ads">
                  <Megaphone className="w-4 h-4 mr-2 text-yellow-400" /> Ads
                </DropdownMenuItem>
                {isOwnerRole && (
                  <DropdownMenuItem onClick={() => changeTab('transactions')} className="cursor-pointer" data-testid="nav-financials-transactions">
                    <BarChart3 className="w-4 h-4 mr-2 text-blue-400" /> Transactions
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Standalone items */}
            {isOwnerRole && (
              <button onClick={() => changeTab('settings')} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'settings' ? 'bg-[#8B5CF6]/20 text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'}`} data-testid="nav-settings">
                <Settings className="w-4 h-4" /> Settings
              </button>
            )}
            <button onClick={() => changeTab('live-audience')} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'live-audience' ? 'bg-[#8B5CF6]/20 text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'}`} data-testid="nav-live-audience">
              <Radio className="w-4 h-4" /> Live Audience
            </button>
            {isOwnerRole && (
              <button onClick={() => changeTab('legal-privacy')} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'legal-privacy' ? 'bg-[#8B5CF6]/20 text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'}`} data-testid="nav-legal-privacy">
                <Shield className="w-4 h-4" /> Legal & Privacy
              </button>
            )}
            <button onClick={() => changeTab('agreements')} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'agreements' ? 'bg-[#8B5CF6]/20 text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'}`} data-testid="nav-agreements">
              <FileText className="w-4 h-4" /> Agreements
            </button>
            <button onClick={() => changeTab('reports')} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'reports' ? 'bg-red-500/20 text-red-400' : 'text-gray-400 hover:text-white hover:bg-white/5'}`} data-testid="nav-reports">
              <Flag className="w-4 h-4" /> Reports
            </button>
          </nav>

          {/* ==================== CONTENT: TO REVIEW ==================== */}
          <TabsContent value="content-review">
            <FilterBar filters={reviewFilters} active={activeFilter} onChange={changeFilter} testIdPrefix="review" />
            <div className="space-y-8">
              {/* Pending Films */}
              {(activeFilter === 'all' || activeFilter === 'films') && (
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Film className="w-5 h-5 text-[#8B5CF6]" /> Pending Movies
                  {filteredReviewFilms.length > 0 && <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full">{filteredReviewFilms.length}</span>}
                </h3>
                {filteredReviewFilms.length > 0 ? (
                  <div className="space-y-4">
                    {filteredReviewFilms.map((film) => <ContentReviewCard key={film.film_id} item={film} type="film" />)}
                  </div>
                ) : (
                  <div className="text-center py-8 admin-card">
                    <CheckCircle className="w-10 h-10 mx-auto text-[#00FF9D] mb-3" />
                    <p className="text-gray-400 text-sm">No pending film submissions</p>
                  </div>
                )}
              </div>
              )}

              {/* Pending Series */}
              {(activeFilter === 'all' || activeFilter === 'series') && (
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Tv className="w-5 h-5 text-[#8B5CF6]" /> Pending Series
                  {filteredReviewSeries.length > 0 && <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full">{filteredReviewSeries.length}</span>}
                </h3>
                {filteredReviewSeries.length > 0 ? (
                  <div className="space-y-4">
                    {filteredReviewSeries.map((s) => <ContentReviewCard key={s.film_id} item={s} type="series" />)}
                  </div>
                ) : (
                  <div className="text-center py-8 admin-card">
                    <CheckCircle className="w-10 h-10 mx-auto text-[#00FF9D] mb-3" />
                    <p className="text-gray-400 text-sm">No pending series submissions</p>
                  </div>
                )}
              </div>
              )}
            </div>
          </TabsContent>

          {/* ==================== EDIT REQUESTS ==================== */}
          <TabsContent value="edit-requests">
            <div className="space-y-6">
              <div className="admin-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-orange-400" />
                    Edit Requests
                    {editRequests.filter(r => r.status === 'pending').length > 0 && (
                      <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-full">
                        {editRequests.filter(r => r.status === 'pending').length} pending
                      </span>
                    )}
                  </h3>
                  <div className="flex gap-1">
                    {['pending', 'approved', 'rejected', 'all'].map(f => (
                      <button key={f} onClick={() => setEditRequestFilter(f)}
                        className={`text-xs px-3 py-1.5 rounded-md transition-colors ${
                          editRequestFilter === f ? 'bg-[#8B5CF6] text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                        }`} data-testid={`edit-req-filter-${f}`}>
                        {f.charAt(0).toUpperCase() + f.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {editRequests.filter(r => editRequestFilter === 'all' || r.status === editRequestFilter).length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No {editRequestFilter !== 'all' ? editRequestFilter : ''} edit requests</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {editRequests.filter(r => editRequestFilter === 'all' || r.status === editRequestFilter).map(req => (
                      <div key={req.request_id} className="p-4 rounded-lg bg-[#0F0B1E] border border-white/5" data-testid={`edit-req-${req.request_id}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                req.type === 'episode_edit' ? 'bg-[#00FF9D]/20 text-[#00FF9D]' : 'bg-[#8B5CF6]/20 text-[#8B5CF6]'
                              }`}>
                                {req.type === 'episode_edit' ? 'EPISODE' : 'SETTINGS'}
                              </span>
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                req.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                req.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                                'bg-red-500/20 text-red-400'
                              }`}>{req.status.toUpperCase()}</span>
                              <span className="text-xs text-gray-500">by {req.creator_name}</span>
                            </div>

                            <div className="grid md:grid-cols-2 gap-3 text-sm">
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Current</p>
                                {Object.entries(req.current_values || {}).map(([k, v]) => (
                                  <p key={k} className="text-gray-400 text-xs"><span className="text-gray-600">{k}:</span> {v || '—'}</p>
                                ))}
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Proposed</p>
                                {Object.entries(req.proposed_values || {}).map(([k, v]) => {
                                  const changed = v !== (req.current_values || {})[k];
                                  return (
                                    <p key={k} className={`text-xs ${changed ? 'text-white font-medium' : 'text-gray-400'}`}>
                                      <span className="text-gray-600">{k}:</span> {v || '—'}
                                    </p>
                                  );
                                })}
                              </div>
                            </div>
                            <p className="text-[10px] text-gray-600 mt-2">{new Date(req.created_at).toLocaleString('en-US')}</p>
                          </div>

                          {req.status === 'pending' && (
                            <div className="flex gap-2 flex-shrink-0">
                              <Button size="sm" className="bg-[#00FF9D]/20 text-[#00FF9D] hover:bg-[#00FF9D]/30"
                                data-testid={`approve-edit-${req.request_id}`}
                                onClick={() => setEditReqAction({ id: req.request_id, action: 'approve', name: req.entity_name || req.type })}>
                                <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve
                              </Button>
                              <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                data-testid={`reject-edit-${req.request_id}`}
                                onClick={() => setEditReqAction({ id: req.request_id, action: 'reject', name: req.entity_name || req.type })}>
                                <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                              </Button>
                            </div>
                          )}
                          {req.status !== 'pending' && req.review_reason && (
                            <p className="text-xs text-gray-500 mt-1 italic">Reason: {req.review_reason}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ==================== CONTENT: PUBLISHED ==================== */}
          <TabsContent value="content-published">
            <FilterBar filters={publishedFilters} active={activeFilter} onChange={changeFilter} testIdPrefix="published" />
            <div className="space-y-8">
              {/* Published Films */}
              {(activeFilter === 'all' || activeFilter === 'films') && (
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Film className="w-5 h-5 text-[#8B5CF6]" /> Published Movies
                  <span className="text-xs bg-[#8B5CF6]/20 text-[#8B5CF6] px-2 py-0.5 rounded-full">{filteredPublishedFilms.length}</span>
                </h3>
                <div className="admin-card p-3 mb-4 border-blue-500/20 bg-blue-500/5">
                  <p className="text-xs text-gray-400">Manage all approved films. Delete functionality removes the film and all associated data.</p>
                </div>
                {filteredPublishedFilms.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Thumbnail</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Title</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Creator</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Views</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Rating</th>
                          <th className="text-right py-3 px-2 text-sm font-medium text-gray-400">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPublishedFilms.map((film) => (
                          <tr key={film.film_id} className="border-b border-white/5" data-testid={`film-row-${film.film_id}`}>
                            <td className="py-3 px-2">
                              <img src={film.thumbnail} alt={film.title} className="w-20 h-12 object-cover rounded" />
                            </td>
                            <td className="py-3 px-2">
                              <p className="font-medium">{film.title}</p>
                              <p className="text-xs text-gray-500">{film.year}</p>
                            </td>
                            <td className="py-3 px-2 text-sm text-gray-300">{film.creator}</td>
                            <td className="py-3 px-2 text-sm text-gray-300">{film.views?.toLocaleString()}</td>
                            <td className="py-3 px-2 text-sm text-gray-300">{film.avg_rating?.toFixed(1)} ({film.rating_count})</td>
                            <td className="py-3 px-2 text-right">
                              <div className="flex gap-1 justify-end">
                                <Button onClick={() => handleViewAnalytics(film.film_id)} size="sm" variant="outline" className="border-[#8B5CF6]/30 text-[#8B5CF6] hover:bg-[#8B5CF6]/10" data-testid={`analytics-film-${film.film_id}`}>
                                  <BarChart3 className="w-4 h-4" />
                                </Button>
                                <Button onClick={() => handleOpenEdit(film)} size="sm" variant="outline" className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10" data-testid={`edit-film-${film.film_id}`}>
                                  <Settings className="w-4 h-4" />
                                </Button>
                                <Button onClick={() => setDeleteDialog({ open: true, type: 'film', id: film.film_id, name: film.title })} size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`delete-film-${film.film_id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 admin-card">
                    <Film className="w-10 h-10 mx-auto text-gray-600 mb-3" />
                    <p className="text-gray-400 text-sm">No approved films</p>
                  </div>
                )}
              </div>
              )}

              {/* Published Series */}
              {(activeFilter === 'all' || activeFilter === 'series') && (
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Tv className="w-5 h-5 text-[#8B5CF6]" /> Published Series
                  <span className="text-xs bg-[#8B5CF6]/20 text-[#8B5CF6] px-2 py-0.5 rounded-full">{filteredPublishedSeries.length}</span>
                </h3>
                <div className="admin-card p-3 mb-4 border-blue-500/20 bg-blue-500/5">
                  <p className="text-xs text-gray-400">Manage all approved series. Click "Episodes" to view and manage individual episodes.</p>
                </div>
                {filteredPublishedSeries.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Thumbnail</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Title</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Creator</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Episodes</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Views</th>
                          <th className="text-right py-3 px-2 text-sm font-medium text-gray-400">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPublishedSeries.map((series) => (
                          <tr key={series.film_id} className="border-b border-white/5" data-testid={`series-row-${series.film_id}`}>
                            <td className="py-3 px-2">
                              <img src={series.thumbnail} alt={series.title} className="w-20 h-12 object-cover rounded" />
                            </td>
                            <td className="py-3 px-2">
                              <p className="font-medium">{series.title}</p>
                              <p className="text-xs text-gray-500">{series.year}</p>
                            </td>
                            <td className="py-3 px-2 text-sm text-gray-300">{series.creator}</td>
                            <td className="py-3 px-2">
                              <Button onClick={() => handleViewEpisodes(series)} size="sm" variant="outline" className="border-[#8B5CF6]/30 text-[#8B5CF6]" data-testid={`view-episodes-${series.film_id}`}>
                                {series.episode_count} Episodes
                              </Button>
                            </td>
                            <td className="py-3 px-2 text-sm text-gray-300">{series.views?.toLocaleString()}</td>
                            <td className="py-3 px-2 text-right">
                              <div className="flex gap-1 justify-end">
                                <Button onClick={() => handleViewAnalytics(series.film_id)} size="sm" variant="outline" className="border-[#8B5CF6]/30 text-[#8B5CF6] hover:bg-[#8B5CF6]/10" data-testid={`analytics-series-${series.film_id}`}>
                                  <BarChart3 className="w-4 h-4" />
                                </Button>
                                <Button onClick={() => handleOpenEdit(series)} size="sm" variant="outline" className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10" data-testid={`edit-series-${series.film_id}`}>
                                  <Settings className="w-4 h-4" />
                                </Button>
                                <Button onClick={() => setDeleteDialog({ open: true, type: 'series', id: series.film_id, name: series.title })} size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`delete-series-${series.film_id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 admin-card">
                    <Tv className="w-10 h-10 mx-auto text-gray-600 mb-3" />
                    <p className="text-gray-400 text-sm">No approved series</p>
                  </div>
                )}
              </div>
              )}
            </div>
          </TabsContent>

          {/* ==================== PEOPLE: CREATORS ==================== */}
          <TabsContent value="people-creators">
            <FilterBar filters={creatorFilters} active={activeFilter} onChange={changeFilter} testIdPrefix="creators" />
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                placeholder="Search creators by name or email..."
                value={peopleSearch}
                onChange={e => setPeopleSearch(e.target.value)}
                className="pl-9 bg-[#030014] border-white/10 h-10"
                data-testid="creators-search-input"
              />
            </div>
            <div className="space-y-8">
              {/* Pending Creator Applications */}
              {(activeFilter === 'all' || activeFilter === 'pending') && pendingCompanies.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-400" /> Pending Creator Applications
                    <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full">{pendingCompanies.length}</span>
                  </h3>
              <div className="space-y-4">
                {pendingCompanies.map((company) => (
                  <div key={company.company_id} className="admin-card p-6" data-testid={`pending-company-${company.company_id}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-lg font-semibold">{company.name}</h3>
                          <span className="px-2 py-0.5 rounded text-xs bg-yellow-500/20 text-yellow-400">Pending</span>
                        </div>
                        {(company.first_name || company.last_name) && (
                          <p className="text-gray-300 text-sm">
                            <User className="w-3.5 h-3.5 inline mr-1.5 text-gray-500" />
                            {company.first_name} {company.last_name}
                          </p>
                        )}
                        <p className="text-gray-400 text-sm mt-0.5">
                          <Mail className="w-3.5 h-3.5 inline mr-1.5 text-gray-500" />
                          {company.contact_email}
                        </p>
                        {company.website && (
                          <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-[#8B5CF6] text-sm hover:underline flex items-center gap-1 mt-0.5">
                            <LinkIcon className="w-3.5 h-3.5" /> {company.website}
                          </a>
                        )}
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button onClick={() => setApproveDialog({ open: true, type: 'company', id: company.company_id, name: company.name })} className="bg-[#00FF9D]/20 text-[#00FF9D] hover:bg-[#00FF9D]/30" data-testid={`approve-company-${company.company_id}`}>
                          <CheckCircle className="w-4 h-4 mr-2" /> Approve
                        </Button>
                        <Button onClick={() => setRejectDialog({ open: true, type: 'company', id: company.company_id })} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`reject-company-${company.company_id}`}>
                          <XCircle className="w-4 h-4 mr-2" /> Reject
                        </Button>
                      </div>
                    </div>

                    {/* Content Type */}
                    {company.content_type_description && (
                      <div className="mb-3 p-3 bg-[#0a0a1f] rounded-lg border border-white/5">
                        <p className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wider">Content Type</p>
                        <p className="text-gray-300 text-sm">{company.content_type_description}</p>
                      </div>
                    )}

                    {/* Description / Bio */}
                    {company.description && company.description !== company.content_type_description && (
                      <div className="mb-3 p-3 bg-[#0a0a1f] rounded-lg border border-white/5">
                        <p className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wider">About</p>
                        <p className="text-gray-300 text-sm">{company.description}</p>
                      </div>
                    )}

                    {/* Social Links */}
                    {(company.instagram || company.facebook || company.youtube) && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {company.instagram && (
                          <a href={company.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-pink-500/10 text-pink-400 text-xs hover:bg-pink-500/20 transition-colors" data-testid="social-instagram">
                            <Instagram className="w-3.5 h-3.5" /> Instagram
                          </a>
                        )}
                        {company.facebook && (
                          <a href={company.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-500/10 text-blue-400 text-xs hover:bg-blue-500/20 transition-colors" data-testid="social-facebook">
                            <Facebook className="w-3.5 h-3.5" /> Facebook
                          </a>
                        )}
                        {company.youtube && (
                          <a href={company.youtube} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/10 text-red-400 text-xs hover:bg-red-500/20 transition-colors" data-testid="social-youtube">
                            <Youtube className="w-3.5 h-3.5" /> YouTube
                          </a>
                        )}
                      </div>
                    )}

                    {/* Sample Films */}
                    {company.sample_films?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wider">Sample Works ({company.sample_films.length})</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {company.sample_films.map((sf, i) => (
                            <div key={i} className="bg-[#0a0a1f] rounded-lg border border-white/5 overflow-hidden">
                              {sf.youtube_id && (
                                <div className="aspect-video">
                                  <iframe
                                    src={`https://www.youtube.com/embed/${sf.youtube_id}`}
                                    title={sf.title || `Sample ${i + 1}`}
                                    className="w-full h-full"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                                    allowFullScreen
                                  />
                                </div>
                              )}
                              {sf.thumbnail && !sf.youtube_id && (
                                <img src={sf.thumbnail} alt={sf.title} className="w-full aspect-video object-cover" />
                              )}
                              <div className="p-3">
                                <p className="font-medium text-sm">{sf.title}</p>
                                {sf.description && <p className="text-xs text-gray-400 mt-1 line-clamp-2">{sf.description}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <p className="text-xs text-gray-500">Submitted: {new Date(company.created_at).toLocaleDateString('en-US')}</p>
                  </div>
                ))}
              </div>
                </div>
              )}

              {/* All Creators */}
              {(activeFilter === 'all' || activeFilter === 'approved' || activeFilter === 'rejected') && (
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#8B5CF6]" /> {activeFilter === 'all' ? 'All' : activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)} Creators
                  <span className="text-xs bg-[#8B5CF6]/20 text-[#8B5CF6] px-2 py-0.5 rounded-full">{filteredCreators.filter(c => !c._isPending || activeFilter !== 'all').length}</span>
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Creator</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Contact</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Status</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Registered</th>
                        <th className="text-right py-3 px-2 text-sm font-medium text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCreators.filter(c => !c._isPending || activeFilter !== 'all').map((company) => (
                        <tr key={company.company_id} className="border-b border-white/5" data-testid={`company-row-${company.company_id}`}>
                          <td className="py-3 px-2">
                            <p className="font-medium">{company.name}</p>
                            <p className="text-xs text-gray-500 line-clamp-1">{company.description}</p>
                          </td>
                          <td className="py-3 px-2 text-sm text-gray-300">{company.contact_email}</td>
                          <td className="py-3 px-2">{getStatusBadge(company.status)}</td>
                          <td className="py-3 px-2 text-sm text-gray-400">{new Date(company.created_at).toLocaleDateString('en-US')}</td>
                          <td className="py-3 px-2 text-right flex gap-2 justify-end">
                            <Button onClick={() => openPolicyHistory(company.user_id, company.name)} size="sm" variant="outline" className="border-[#8B5CF6]/30 text-[#8B5CF6] text-xs" data-testid={`policy-history-creator-${company.company_id}`}>
                              Policies
                            </Button>
                            <Button onClick={() => handleViewAgreements(company)} size="sm" variant="outline" className="border-[#00FF9D]/30 text-[#00FF9D]" data-testid={`agreements-btn-${company.company_id}`}>
                              <FileText className="w-4 h-4 mr-1" /> Agreements
                            </Button>
                            <Button onClick={() => handleViewCompanyAnalytics(company)} size="sm" variant="outline" className="border-[#8B5CF6]/30">
                              <BarChart3 className="w-4 h-4" />
                            </Button>
                            <Button onClick={() => setDeleteDialog({ open: true, type: 'company', id: company.company_id, name: company.name })} size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`delete-company-${company.company_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              )}
            </div>
          </TabsContent>

          {/* Users */}
          <TabsContent value="users">
            <FilterBar filters={userFilters} active={activeFilter} onChange={changeFilter} testIdPrefix="users" />
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                placeholder="Search users by name or email..."
                value={peopleSearch}
                onChange={e => setPeopleSearch(e.target.value)}
                className="pl-9 bg-[#030014] border-white/10 h-10"
                data-testid="users-search-input"
              />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">User</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Role</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Status</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Subscription</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Joined</th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u.user_id} className={`border-b border-white/5 ${u.is_banned ? 'opacity-60 bg-red-500/5' : ''}`} data-testid={`user-row-${u.user_id}`}>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-3">
                          <img src={u.picture || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=150&q=80'} alt={u.name} className="w-8 h-8 rounded-full object-cover" />
                          <div>
                            <p className="font-medium">{u.name}</p>
                            <p className="text-xs text-gray-500">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <span className={`badge text-xs ${u.role === 'owner' ? 'bg-[#00FF9D]/20 text-[#00FF9D]' : u.role === 'admin' ? 'bg-[#00FF9D]/20 text-[#00FF9D]' : u.role === 'company' ? 'bg-blue-500/20 text-blue-400' : 'bg-white/10 text-gray-400'}`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="py-3 px-2">
                        {u.is_banned ? (
                          <span className="badge text-xs bg-red-500/20 text-red-400">Banned</span>
                        ) : (
                          <span className="badge text-xs bg-green-500/20 text-green-400">Active</span>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        {u.subscription ? (
                          <span className="badge bg-[#8B5CF6]/20 text-[#8B5CF6] text-xs">{u.subscription.plan}</span>
                        ) : (
                          <span className="text-gray-500 text-sm">Free</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-sm text-gray-400">{new Date(u.created_at).toLocaleDateString('en-US')}</td>
                      <td className="py-3 px-2 text-right">
                        <div className="flex items-center gap-1.5 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#8B5CF6]/30 text-[#8B5CF6] hover:bg-[#8B5CF6]/10 text-xs"
                            onClick={() => openPolicyHistory(u.user_id, u.name || u.email)}
                            data-testid={`policy-history-${u.user_id}`}
                          >
                            Policies
                          </Button>
                          {u.role !== 'owner' && u.role !== 'admin' && (
                            u.is_banned ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                                onClick={async () => {
                                  try {
                                    await ownerApi.unbanUser(u.user_id);
                                    toast.success(`${u.name} unbanned`);
                                    fetchAllData();
                                  } catch(e) { toast.error(e.message); }
                                }}
                                data-testid={`unban-user-${u.user_id}`}
                              >
                                Unban
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                onClick={() => setBanDialog({ open: true, user: u })}
                                data-testid={`ban-user-${u.user_id}`}
                              >
                                Ban
                              </Button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Subscriptions Management */}
          <TabsContent value="subscriptions">
            <FilterBar filters={subFilters} active={activeFilter} onChange={changeFilter} testIdPrefix="subscriptions" />
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500 whitespace-nowrap">From</label>
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                  className="bg-[#030014] border-white/10 h-9 text-sm w-40" data-testid="fin-date-from" />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500 whitespace-nowrap">To</label>
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                  className="bg-[#030014] border-white/10 h-9 text-sm w-40" data-testid="fin-date-to" />
              </div>
              {(dateFrom || dateTo) && (
                <button onClick={() => { setDateFrom(''); setDateTo(''); }}
                  className="text-xs text-[#8B5CF6] hover:underline" data-testid="fin-date-clear">Clear dates</button>
              )}
            </div>
            <div className="admin-card p-4 mb-4 border-blue-500/30 bg-blue-500/5">
              <p className="text-sm text-gray-300">Manage all subscriptions. Delete will cancel the subscription immediately.</p>
            </div>
            {filteredSubscriptions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">User ID</th>
                      <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Plan</th>
                      <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Status</th>
                      <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Created</th>
                      <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Expires</th>
                      <th className="text-right py-3 px-2 text-sm font-medium text-gray-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubscriptions.map((sub) => (
                      <tr key={sub.subscription_id} className="border-b border-white/5" data-testid={`sub-row-${sub.subscription_id}`}>
                        <td className="py-3 px-2 text-sm text-gray-300">{sub.user_id}</td>
                        <td className="py-3 px-2">
                          <span className="badge bg-[#8B5CF6]/20 text-[#8B5CF6] text-xs">{sub.plan}</span>
                        </td>
                        <td className="py-3 px-2">{getStatusBadge(sub.status)}</td>
                        <td className="py-3 px-2 text-sm text-gray-400">{new Date(sub.created_at).toLocaleDateString('en-US')}</td>
                        <td className="py-3 px-2 text-sm text-gray-400">{sub.expires_at ? new Date(sub.expires_at).toLocaleDateString('en-US') : '-'}</td>
                        <td className="py-3 px-2 text-right">
                          <Button 
                            onClick={() => setDeleteDialog({ open: true, type: 'subscription', id: sub.subscription_id, name: `${sub.plan} subscription` })} 
                            size="sm" 
                            variant="outline" 
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                            data-testid={`delete-sub-${sub.subscription_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <CreditCard className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                <p className="text-gray-400">No subscriptions</p>
              </div>
            )}
          </TabsContent>

          {/* Revenue */}
          <TabsContent value="revenue">
            {/* Period Filter Buttons */}
            <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-2 mb-4" data-testid="revenue-period-filters">
              <div className="flex items-center gap-1.5 flex-wrap">
                <Filter className="w-3.5 h-3.5 text-gray-500 mr-1 hidden sm:block" />
                {[
                  { value: 'all', label: 'All time' },
                  { value: 'today', label: 'Today' },
                  { value: '1week', label: 'Last 7 days' },
                  { value: '1month', label: 'Last 30 days' },
                  { value: 'custom', label: 'Custom' },
                ].map((p) => (
                  <button key={p.value}
                    onClick={() => p.value === 'custom' ? setRevenuePeriod('custom') : handlePeriodSelect(p.value)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${revenuePeriod === p.value ? 'bg-[#8B5CF6]/20 text-[#8B5CF6] ring-1 ring-[#8B5CF6]/30' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                    data-testid={`rev-period-${p.value}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              {revenuePeriod === 'custom' && (
                <div className="flex flex-wrap items-center gap-2 mt-1 sm:mt-0">
                  <Input type="date" value={customDateFrom} onChange={e => setCustomDateFrom(e.target.value)}
                    className="bg-[#030014] border-white/10 h-8 text-xs w-36" data-testid="rev-custom-from" />
                  <span className="text-xs text-gray-500">–</span>
                  <Input type="date" value={customDateTo} onChange={e => setCustomDateTo(e.target.value)}
                    className="bg-[#030014] border-white/10 h-8 text-xs w-36" data-testid="rev-custom-to" />
                  <button
                    onClick={() => { if (customDateFrom && customDateTo) handlePeriodSelect('custom'); else toast.error('Select both dates'); }}
                    disabled={!customDateFrom || !customDateTo}
                    className="px-3 py-1.5 rounded-md text-xs font-medium bg-[#8B5CF6] text-white hover:bg-[#7C3AED] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    data-testid="rev-custom-apply"
                  >Apply</button>
                </div>
              )}
              {revenueLoading && <Loader2 className="w-4 h-4 animate-spin text-[#8B5CF6] ml-2" />}
            </div>

            {revenueData && (
              <div className="space-y-6">
                {/* Revenue Summary Cards - Updated */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div className="admin-card p-4 text-center">
                    <DollarSign className="w-5 h-5 text-green-400 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-[#00FF9D]">${revenueData.total_revenue?.toFixed(2) || '0.00'}</p>
                    <p className="text-xs text-gray-400">{revenuePeriod === 'all' ? 'All Time Revenue' : revenuePeriod === 'today' ? 'Today' : revenuePeriod === '1week' ? 'Last 7 Days' : revenuePeriod === '1month' ? 'Last 30 Days' : 'Selected Period'}</p>
                  </div>
                  <div className="admin-card p-4 text-center">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      {revenueData.month_over_month_growth >= 0 ? (
                        <ArrowUpRight className="w-5 h-5 text-[#00FF9D]" />
                      ) : (
                        <ArrowDownRight className="w-5 h-5 text-red-400" />
                      )}
                    </div>
                    <p className={`text-2xl font-bold ${revenueData.month_over_month_growth >= 0 ? 'text-[#00FF9D]' : 'text-red-400'}`}>
                      {revenueData.month_over_month_growth > 0 ? '+' : ''}{revenueData.month_over_month_growth || 0}%
                    </p>
                    <p className="text-xs text-gray-400">MoM Growth</p>
                  </div>
                  <div className="admin-card p-4 text-center">
                    <Users className="w-5 h-5 text-[#8B5CF6] mx-auto mb-1" />
                    <p className="text-2xl font-bold">{revenueData.active_premium || revenueData.active_subscribers || 0}</p>
                    <p className="text-xs text-gray-400">Premium Subscribers</p>
                  </div>
                  <div className="admin-card p-4 text-center">
                    <TrendingUp className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                    <p className="text-2xl font-bold">{revenueData.new_subscribers_this_month || 0}</p>
                    <p className="text-xs text-gray-400">New This Month</p>
                  </div>
                  <div className="admin-card p-4 text-center">
                    <p className={`text-2xl font-bold ${revenueData.churn_rate > 10 ? 'text-red-400' : 'text-gray-300'}`}>
                      {revenueData.churn_rate || 0}%
                    </p>
                    <p className="text-xs text-gray-400">Churn Rate</p>
                  </div>
                  <div className="admin-card p-4 text-center">
                    <p className="text-2xl font-bold text-white">{revenueData.total_transactions || 0}</p>
                    <p className="text-xs text-gray-400">Total Transactions</p>
                  </div>
                </div>

                {/* Revenue Streams Breakdown - TABLE */}
                <div className="admin-card p-4 border-[#8B5CF6]/30 bg-[#8B5CF6]/5" data-testid="streams-breakdown">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-[#8B5CF6]" />
                    Revenue Streams Breakdown
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 px-3 font-medium text-gray-400">Stream</th>
                          <th className="text-right py-3 px-3 font-medium text-gray-400">Platform %</th>
                          <th className="text-right py-3 px-3 font-medium text-gray-400">Gross Revenue</th>
                          <th className="text-right py-3 px-3 font-medium text-[#8B5CF6]">Distributed to Creators</th>
                          <th className="text-right py-3 px-3 font-medium text-[#00FF9D]">Net Platform Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(revenueData.streams_breakdown || []).map((s) => (
                          <tr key={s.stream} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                            <td className="py-3 px-3 font-medium text-white">{s.stream}</td>
                            <td className="py-3 px-3 text-right text-gray-400">{s.rate}</td>
                            <td className="py-3 px-3 text-right text-gray-300">{'\u0024'}{s.gross.toFixed(2)}</td>
                            <td className="py-3 px-3 text-right text-[#8B5CF6]">{'\u0024'}{s.distributed.toFixed(2)}</td>
                            <td className="py-3 px-3 text-right text-[#00FF9D] font-semibold">{'\u0024'}{s.net_platform.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                      {revenueData.streams_totals && (
                        <tfoot>
                          <tr className="border-t-2 border-[#8B5CF6]/30 font-bold">
                            <td className="py-3 px-3 text-white" colSpan={2}>Total</td>
                            <td className="py-3 px-3 text-right text-white">{'\u0024'}{revenueData.streams_totals.gross.toFixed(2)}</td>
                            <td className="py-3 px-3 text-right text-[#8B5CF6]">{'\u0024'}{revenueData.streams_totals.distributed.toFixed(2)}</td>
                            <td className="py-3 px-3 text-right text-[#00FF9D]">{'\u0024'}{revenueData.streams_totals.net_platform.toFixed(2)}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>

                {/* Donation Stats */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="admin-card p-5">
                    <h4 className="text-sm font-semibold text-gray-400 mb-3">Donation Revenue Details</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Gross Donations (Total)</span>
                        <span className="font-bold">${revenueData.total_donations_gross?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Platform Commission ({revenueData.donation_commission_rate || 10}%)</span>
                        <span className="font-bold text-[#00FF9D]">${revenueData.total_donations_commission?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="flex justify-between items-center border-t border-white/10 pt-3">
                        <span className="text-gray-500">Paid to Creators (90%)</span>
                        <span className="font-bold">${((revenueData.total_donations_gross || 0) - (revenueData.total_donations_commission || 0)).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="admin-card p-5">
                    <h4 className="text-sm font-semibold text-gray-400 mb-3">Product Sales Revenue Details</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Platform Commission ({revenueData.product_commission_rate || 10}%)</span>
                        <span className="font-bold text-blue-400">${revenueData.total_products_commission?.toFixed(2) || '0.00'}</span>
                      </div>
                      <p className="text-xs text-gray-600 italic">E-commerce shop coming soon. Creators will sell merchandise with {revenueData.product_commission_rate || 10}% platform fee.</p>
                    </div>
                  </div>
                </div>
                
                {/* Ad Revenue Details */}
                <div className="admin-card p-5">
                  <h4 className="text-sm font-semibold text-gray-400 mb-3">Ad Revenue Details</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Total Ad Impressions</span>
                      <span className="font-bold">{revenueData.total_ad_impressions || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Total Ad Clicks</span>
                      <span className="font-bold">{revenueData.total_ad_clicks || 0}</span>
                    </div>
                    <div className="flex justify-between items-center border-t border-white/10 pt-3">
                      <span className="text-gray-500">Total Ad Revenue</span>
                      <span className="font-bold text-yellow-400">${revenueData.total_ad_revenue?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>
                </div>

                {/* Revenue Chart - Updated */}
                {revenueData.chart_data && (
                  <div className="admin-card p-6">
                    <h3 className="text-lg font-semibold mb-4">Revenue by Stream (Last 12 Months)</h3>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={revenueData.chart_data}>
                          <defs>
                            <linearGradient id="gradSubs" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.4} />
                              <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="gradDonations" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#00FF9D" stopOpacity={0.4} />
                              <stop offset="95%" stopColor="#00FF9D" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="gradProducts" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.4} />
                              <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1A1033" />
                          <XAxis dataKey="month" stroke="#64748b" fontSize={11} />
                          <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => `$${v}`} />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#0F0B1E',
                              border: '1px solid rgba(139, 92, 246, 0.3)',
                              borderRadius: '8px',
                              color: '#fff',
                            }}
                            formatter={(value) => [`$${value?.toFixed(2) || '0.00'}`, undefined]}
                          />
                          <Legend />
                          <Area type="monotone" dataKey="subscriptions" stroke="#8B5CF6" fillOpacity={1} fill="url(#gradSubs)" strokeWidth={2} name="Subscriptions" />
                          <Area type="monotone" dataKey="donations" stroke="#00FF9D" fillOpacity={1} fill="url(#gradDonations)" strokeWidth={2} name="Donations" />
                          <Area type="monotone" dataKey="products" stroke="#3B82F6" fillOpacity={1} fill="url(#gradProducts)" strokeWidth={2} name="Products" />
                          <Area type="monotone" dataKey="ads" stroke="#EAB308" fillOpacity={0.3} fill="#EAB30833" strokeWidth={2} name="Ads" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Monthly Revenue History */}
                <div className="admin-card p-6">
                  <h3 className="text-lg font-semibold mb-4">Monthly Revenue History</h3>
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-2 text-gray-400">Month</th>
                          <th className="text-right py-2 text-gray-400">Subscriptions</th>
                          <th className="text-right py-2 text-gray-400">Donations</th>
                          <th className="text-right py-2 text-gray-400">Products</th>
                          <th className="text-right py-2 text-gray-400">Ads</th>
                          <th className="text-right py-2 text-gray-400">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {revenueData.monthly_revenue?.map((m) => (
                          <tr key={m.month} className="border-b border-white/5">
                            <td className="py-2 font-medium">{m.month}</td>
                            <td className="py-2 text-right text-[#8B5CF6]">${(m.subscriptions || 0).toFixed(2)}</td>
                            <td className="py-2 text-right text-[#00FF9D]">${(m.donations || 0).toFixed(2)}</td>
                            <td className="py-2 text-right text-blue-400">${(m.products || 0).toFixed(2)}</td>
                            <td className="py-2 text-right text-yellow-400">${(m.ads || 0).toFixed(2)}</td>
                            <td className="py-2 text-right font-bold">${(m.total || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Transactions Tab - Owner Only */}
          {isOwnerRole && (
            <TabsContent value="transactions">
              <FilterBar filters={txFilters} active={activeFilter} onChange={changeFilter} testIdPrefix="transactions" />
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500 whitespace-nowrap">From</label>
                  <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                    className="bg-[#030014] border-white/10 h-9 text-sm w-40" data-testid="tx-date-from" />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500 whitespace-nowrap">To</label>
                  <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                    className="bg-[#030014] border-white/10 h-9 text-sm w-40" data-testid="tx-date-to" />
                </div>
                {(dateFrom || dateTo) && (
                  <button onClick={() => { setDateFrom(''); setDateTo(''); }}
                    className="text-xs text-[#8B5CF6] hover:underline" data-testid="tx-date-clear">Clear dates</button>
                )}
              </div>
              <div className="space-y-4">
                <div className="admin-card p-4 border-blue-500/30 bg-blue-500/5">
                  <p className="text-sm text-gray-300">Complete transaction history — subscriptions, donations, and creator payouts.</p>
                </div>
                {filteredTransactions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Date</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Type</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Description</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Status</th>
                          <th className="text-right py-3 px-2 text-sm font-medium text-gray-400">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTransactions.map((tx, idx) => (
                          <tr key={tx.id || idx} className="border-b border-white/5" data-testid={`tx-row-${idx}`}>
                            <td className="py-3 px-2 text-sm text-gray-400">{tx.created_at ? new Date(tx.created_at).toLocaleDateString('en-US') : '-'}</td>
                            <td className="py-3 px-2">
                              <span className={`badge text-xs ${tx.type === 'subscription' ? 'bg-[#8B5CF6]/20 text-[#8B5CF6]' : tx.type === 'donation' ? 'bg-pink-500/20 text-pink-400' : 'bg-orange-500/20 text-orange-400'}`}>
                                {tx.type}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-sm text-gray-300">{tx.description}</td>
                            <td className="py-3 px-2">
                              <span className={`badge text-xs ${tx.status === 'paid' || tx.status === 'completed' ? 'bg-green-500/20 text-green-400' : tx.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>
                                {tx.status}
                              </span>
                            </td>
                            <td className={`py-3 px-2 text-sm text-right font-medium ${tx.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {tx.amount >= 0 ? '+' : ''}{tx.amount?.toFixed(2)} {tx.currency}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <DollarSign className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                    <p className="text-gray-400">No transactions yet</p>
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          {/* Ads Tab */}
          <TabsContent value="ads">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500 whitespace-nowrap">From</label>
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                  className="bg-[#030014] border-white/10 h-9 text-sm w-40" data-testid="ads-date-from" />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500 whitespace-nowrap">To</label>
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                  className="bg-[#030014] border-white/10 h-9 text-sm w-40" data-testid="ads-date-to" />
              </div>
              {(dateFrom || dateTo) && (
                <button onClick={() => { setDateFrom(''); setDateTo(''); }}
                  className="text-xs text-[#8B5CF6] hover:underline" data-testid="ads-date-clear">Clear dates</button>
              )}
            </div>
            <div className="space-y-6">
              {/* Ad Performance Stats */}
              <div className="grid md:grid-cols-4 gap-4">
                <div className="admin-card p-4">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                    <Eye className="w-4 h-4" />
                    Impressions
                  </div>
                  <p className="text-2xl font-bold">{adStats?.total_impressions?.toLocaleString() || 0}</p>
                </div>
                <div className="admin-card p-4">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                    <MousePointer className="w-4 h-4" />
                    Clicks
                  </div>
                  <p className="text-2xl font-bold">{adStats?.total_clicks?.toLocaleString() || 0}</p>
                  <p className="text-xs text-gray-500">CTR: {adStats?.ctr || 0}%</p>
                </div>
                <div className="admin-card p-4">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                    <SkipForward className="w-4 h-4" />
                    Skips
                  </div>
                  <p className="text-2xl font-bold">{adStats?.total_skips?.toLocaleString() || 0}</p>
                </div>
                <div className="admin-card p-4 border-[#00FF9D]/30">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                    <DollarSign className="w-4 h-4" />
                    Est. Revenue
                  </div>
                  <p className="text-2xl font-bold text-[#00FF9D]">${adStats?.estimated_revenue_usd?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs text-gray-500">~$4 RPM</p>
                </div>
              </div>

              {/* Ad Configuration */}
              <div className="admin-card p-6 border-[#8B5CF6]/30">
                <div className="flex items-center gap-3 mb-6">
                  <Megaphone className="w-6 h-6 text-[#8B5CF6]" />
                  <div>
                    <h3 className="text-lg font-semibold">Ad Configuration</h3>
                    <p className="text-sm text-gray-400">Configure ad placements for free-tier users</p>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Pre-roll Ads */}
                  <div className="p-4 bg-[#0a0a1f] rounded-lg border border-white/5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <Play className="w-5 h-5 text-[#8B5CF6]" />
                        <div>
                          <p className="font-medium">Pre-roll Ads</p>
                          <p className="text-sm text-gray-400">Show ads before video starts</p>
                        </div>
                      </div>
                      <Switch
                        checked={adConfig.pre_roll_enabled}
                        onCheckedChange={(checked) => setAdConfig(prev => ({ ...prev, pre_roll_enabled: checked }))}
                        data-testid="pre-roll-toggle"
                      />
                    </div>
                    {adConfig.pre_roll_enabled && (
                      <div className="grid md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-white/5">
                        <div>
                          <label className="text-sm text-gray-400 mb-2 block">Ad Duration (seconds)</label>
                          <Input
                            type="number"
                            min="3"
                            max="30"
                            value={adConfig.pre_roll_duration}
                            onChange={(e) => setAdConfig(prev => ({ ...prev, pre_roll_duration: parseInt(e.target.value) || 5 }))}
                            className="bg-[#030014] border-white/10"
                          />
                        </div>
                        <div>
                          <label className="text-sm text-gray-400 mb-2 block">Skip After (seconds)</label>
                          <Input
                            type="number"
                            min="3"
                            max="15"
                            value={adConfig.pre_roll_skip_after}
                            onChange={(e) => setAdConfig(prev => ({ ...prev, pre_roll_skip_after: parseInt(e.target.value) || 5 }))}
                            className="bg-[#030014] border-white/10"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Mid-roll Ads */}
                  <div className="p-4 bg-[#0a0a1f] rounded-lg border border-white/5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <Film className="w-5 h-5 text-yellow-400" />
                        <div>
                          <p className="font-medium">Mid-roll Ads</p>
                          <p className="text-sm text-gray-400">Show ads during video playback</p>
                        </div>
                      </div>
                      <Switch
                        checked={adConfig.mid_roll_enabled}
                        onCheckedChange={(checked) => setAdConfig(prev => ({ ...prev, mid_roll_enabled: checked }))}
                        data-testid="mid-roll-toggle"
                      />
                    </div>
                    {adConfig.mid_roll_enabled && (
                      <div className="mt-4 pt-4 border-t border-white/5">
                        <label className="text-sm text-gray-400 mb-2 block">Show ad every (seconds)</label>
                        <Input
                          type="number"
                          min="300"
                          max="1800"
                          step="60"
                          value={adConfig.mid_roll_interval}
                          onChange={(e) => setAdConfig(prev => ({ ...prev, mid_roll_interval: parseInt(e.target.value) || 600 }))}
                          className="bg-[#030014] border-white/10 max-w-xs"
                        />
                        <p className="text-xs text-gray-500 mt-1">Recommended: 600 seconds (10 minutes)</p>
                      </div>
                    )}
                  </div>

                  {/* Banner Ads */}
                  <div className="p-4 bg-[#0a0a1f] rounded-lg border border-white/5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <BarChart3 className="w-5 h-5 text-blue-400" />
                        <div>
                          <p className="font-medium">Banner Ads</p>
                          <p className="text-sm text-gray-400">Show banner during video playback</p>
                        </div>
                      </div>
                      <Switch
                        checked={adConfig.banner_enabled}
                        onCheckedChange={(checked) => setAdConfig(prev => ({ ...prev, banner_enabled: checked }))}
                        data-testid="banner-toggle"
                      />
                    </div>
                  </div>

                  {/* AdSense Integration */}
                  <div className="p-4 bg-[#0a0a1f] rounded-lg border border-yellow-500/20">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded bg-yellow-500/20 flex items-center justify-center">
                          <span className="text-yellow-400 font-bold text-xs">G</span>
                        </div>
                        <div>
                          <p className="font-medium">Google AdSense</p>
                          <p className="text-sm text-gray-400">Connect AdSense for real ad revenue</p>
                        </div>
                      </div>
                      <Switch
                        checked={adConfig.adsense_enabled}
                        onCheckedChange={(checked) => setAdConfig(prev => ({ ...prev, adsense_enabled: checked }))}
                        data-testid="adsense-toggle"
                      />
                    </div>
                    {adConfig.adsense_enabled && (
                      <div className="mt-4 pt-4 border-t border-white/5">
                        <label className="text-sm text-gray-400 mb-2 block">AdSense Publisher ID</label>
                        <Input
                          value={adConfig.adsense_publisher_id || ''}
                          onChange={(e) => setAdConfig(prev => ({ ...prev, adsense_publisher_id: e.target.value }))}
                          placeholder="pub-xxxxxxxxxxxx"
                          className="bg-[#030014] border-yellow-500/20 max-w-md"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Get your Publisher ID from <a href="https://www.google.com/adsense/" target="_blank" rel="noopener noreferrer" className="text-yellow-400 hover:underline">Google AdSense</a>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end pt-4">
                    <Button
                      onClick={handleSaveAdConfig}
                      disabled={savingAdConfig}
                      className="bg-[#8B5CF6] hover:bg-[#8B5CF6]/80"
                      data-testid="save-ad-config-btn"
                    >
                      {savingAdConfig ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Save Ad Configuration
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Ad Stats by Type */}
              <div className="admin-card p-6">
                <h3 className="text-lg font-semibold mb-4">Ads by Type</h3>
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="p-4 bg-[#0a0a1f] rounded-lg text-center">
                    <p className="text-2xl font-bold text-[#8B5CF6]">{adStats?.ads_by_type?.pre_roll || 0}</p>
                    <p className="text-sm text-gray-400">Pre-roll</p>
                  </div>
                  <div className="p-4 bg-[#0a0a1f] rounded-lg text-center">
                    <p className="text-2xl font-bold text-yellow-400">{adStats?.ads_by_type?.mid_roll || 0}</p>
                    <p className="text-sm text-gray-400">Mid-roll</p>
                  </div>
                  <div className="p-4 bg-[#0a0a1f] rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-400">{adStats?.ads_by_type?.banner || 0}</p>
                    <p className="text-sm text-gray-400">Banner</p>
                  </div>
                  <div className="p-4 bg-[#0a0a1f] rounded-lg text-center">
                    <p className="text-2xl font-bold text-[#00FF9D]">{adStats?.ads_by_type?.overlay || 0}</p>
                    <p className="text-sm text-gray-400">Overlay</p>
                  </div>
                </div>
              </div>

              {/* Info Banner */}
              <div className="admin-card p-4 border-blue-500/30 bg-blue-500/5">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-400">Ad System Note</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Currently showing placeholder ads. Connect Google AdSense or create custom ads to display real advertisements 
                      and generate revenue. Premium subscribers ($3.99/mo) will not see any ads.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="space-y-6">
              {/* Stripe: Live mode active — keys managed via environment variables */}
              <div className="admin-card p-6 border-[#00FF9D]/30 mb-6" data-testid="stripe-config-section">
                <div className="flex items-center gap-3">
                  <Wallet className="w-6 h-6 text-[#00FF9D]" />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold">Stripe API</h3>
                    <p className="text-sm text-gray-400">Payment keys are configured via environment variables for security</p>
                  </div>
                  <div className="flex items-center gap-2" data-testid="stripe-connection-status">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#00FF9D] shadow-[0_0_6px_rgba(0,255,157,0.4)]" />
                    <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">Live</span>
                  </div>
                </div>
              </div>

              {/* Platform Info */}
              {/* Creator Payouts Management */}
              <div className="admin-card p-6 border-[#00FF9D]/30">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <DollarSign className="w-6 h-6 text-[#00FF9D]" />
                    <div>
                      <h3 className="text-lg font-semibold">Creator Payouts</h3>
                      <p className="text-sm text-gray-400">Generate and process subscription + ad revenue payouts</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={async () => {
                        setProcessingPayouts(true);
                        try {
                          const result = await ownerApi.generateTestPayouts();
                          toast.success(`Generated ${result.summary.generated} pending payouts ($${result.summary.total_amount})`);
                          fetchAllData();
                        } catch (e) {
                          toast.error(e.message || 'Failed to generate test payouts');
                        } finally {
                          setProcessingPayouts(false);
                        }
                      }}
                      disabled={processingPayouts}
                      variant="outline"
                      className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                      data-testid="generate-test-payouts-btn"
                    >
                      {processingPayouts ? 'Generating...' : 'Generate Test Payouts'}
                    </Button>
                    <Button
                      onClick={async () => {
                        setProcessingPayouts(true);
                        try {
                          const result = await ownerApi.processPayouts();
                          toast.success(`Payouts processed: ${result.summary.processed} completed, ${result.summary.failed} failed`);
                          fetchAllData();
                        } catch (e) {
                          toast.error(e.message || 'Failed to process payouts');
                        } finally {
                          setProcessingPayouts(false);
                        }
                      }}
                      disabled={processingPayouts || !pendingPayouts?.pending_withdrawals?.length}
                      className="bg-[#00FF9D] hover:bg-[#00FF9D]/80 text-black"
                      data-testid="process-payouts-btn"
                    >
                      {processingPayouts ? 'Processing...' : 'Process All Payouts'}
                    </Button>
                  </div>
                </div>

                {/* Payout Summary */}
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="admin-card p-4 text-center">
                    <p className="text-2xl font-bold text-yellow-400">{pendingPayouts?.summary?.count || 0}</p>
                    <p className="text-xs text-gray-400">Pending Payouts</p>
                  </div>
                  <div className="admin-card p-4 text-center">
                    <p className="text-2xl font-bold text-[#00FF9D]">${pendingPayouts?.summary?.total_pending_amount?.toFixed(2) || '0.00'}</p>
                    <p className="text-xs text-gray-400">Total Pending</p>
                  </div>
                  <div className="admin-card p-4 text-center">
                    <p className="text-2xl font-bold text-blue-400">${pendingPayouts?.summary?.total_processing_amount?.toFixed(2) || '0.00'}</p>
                    <p className="text-xs text-gray-400">Being Processed</p>
                  </div>
                </div>

                {/* Pending Withdrawals List */}
                {pendingPayouts?.pending_withdrawals?.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {pendingPayouts.pending_withdrawals.map((w) => (
                      <div key={w.withdrawal_id} className="flex items-center justify-between p-3 bg-[#0a0a1f] rounded-lg">
                        <div>
                          <p className="font-medium">${w.amount.toFixed(2)}</p>
                          <p className="text-xs text-gray-500">{w.method?.toUpperCase()} • {new Date(w.created_at).toLocaleDateString('en-US')}</p>
                          {w.notes && <p className="text-xs text-gray-600 mt-1">{w.notes}</p>}
                        </div>
                        <span className={`badge text-xs ${w.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-blue-500/20 text-blue-400'}`}>
                          {w.status}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-4">No pending payouts. Click "Generate Test Payouts" to calculate and create pending payouts.</p>
                )}
              </div>

              <div className="admin-card p-4 border-yellow-500/30 bg-yellow-500/5">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-400">Test Mode</p>
                    <p className="text-sm text-gray-400 mt-1">
                      1. Click "Generate Test Payouts" to calculate subscription + ad earnings for all creators (March 1 to today).<br/>
                      2. Review the pending payouts list.<br/>
                      3. Click "Process All Payouts" to execute the Stripe transfers.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Featured Content Management */}
          <TabsContent value="featured">
            <div className="space-y-6">
              <div className="admin-card p-4 border-[#8B5CF6]/30 bg-[#8B5CF6]/5">
                <div className="flex items-center gap-3">
                  <Star className="w-5 h-5 text-[#8B5CF6]" />
                  <div>
                    <p className="font-medium">Homepage Featured Carousel</p>
                    <p className="text-sm text-gray-400">Select up to 5 films or series to feature in the homepage hero carousel. They will appear in the order shown below.</p>
                  </div>
                </div>
              </div>

              {/* Currently Selected */}
              <div className="admin-card p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-400" />
                  Selected Featured ({featuredIds.length}/5)
                </h3>
                {featuredIds.length > 0 ? (
                  <div className="space-y-2">
                    {featuredIds.map((fid, index) => {
                      const item = allContent.find(c => c.film_id === fid);
                      if (!item) return null;
                      return (
                        <div key={fid} className="flex items-center gap-3 p-3 bg-[#0a0a1f] rounded-lg border border-white/5" data-testid={`featured-item-${index}`}>
                          <span className="text-lg font-bold text-[#8B5CF6] w-6 text-center">{index + 1}</span>
                          <img src={item.thumbnail} alt={item.title} className="w-20 h-12 object-cover rounded" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{item.title}</p>
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                              <span>{item.content_type === 'series' ? 'Series' : 'Movie'}</span>
                              <span>•</span>
                              <span>{item.creator}</span>
                              {item.genres?.length > 0 && (
                                <>
                                  <span>•</span>
                                  <span>{item.genres.slice(0, 2).join(', ')}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-gray-400 hover:text-white"
                              disabled={index === 0}
                              onClick={() => {
                                const newIds = [...featuredIds];
                                [newIds[index - 1], newIds[index]] = [newIds[index], newIds[index - 1]];
                                setFeaturedIds(newIds);
                              }}
                              data-testid={`featured-move-up-${index}`}
                            >
                              ▲
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-gray-400 hover:text-white"
                              disabled={index === featuredIds.length - 1}
                              onClick={() => {
                                const newIds = [...featuredIds];
                                [newIds[index], newIds[index + 1]] = [newIds[index + 1], newIds[index]];
                                setFeaturedIds(newIds);
                              }}
                              data-testid={`featured-move-down-${index}`}
                            >
                              ▼
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              onClick={() => setFeaturedIds(featuredIds.filter(id => id !== fid))}
                              data-testid={`featured-remove-${index}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No featured content selected. Use the search below to add items.</p>
                )}
                <div className="flex justify-end mt-4">
                  <Button
                    onClick={async () => {
                      setSavingFeatured(true);
                      try {
                        await ownerApi.saveFeaturedConfig(featuredIds);
                        toast.success('Featured content saved!');
                      } catch (e) {
                        toast.error(e.message || 'Failed to save featured content');
                      } finally {
                        setSavingFeatured(false);
                      }
                    }}
                    disabled={savingFeatured}
                    className="bg-[#8B5CF6] hover:bg-[#8B5CF6]/80"
                    data-testid="save-featured-btn"
                  >
                    {savingFeatured ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Save Featured
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Search & Add Content */}
              <div className="admin-card p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Search className="w-5 h-5 text-gray-400" />
                  Search Content
                </h3>
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search by title..."
                      value={featuredSearch}
                      onChange={(e) => setFeaturedSearch(e.target.value)}
                      className="bg-[#030014] border-white/10 pl-10"
                      data-testid="featured-search-input"
                    />
                  </div>
                  <select
                    value={featuredGenreFilter}
                    onChange={(e) => setFeaturedGenreFilter(e.target.value)}
                    className="bg-[#030014] border border-white/10 rounded-md px-3 py-2 text-sm text-gray-300 min-w-[160px]"
                    data-testid="featured-genre-filter"
                  >
                    <option value="">All Genres</option>
                    {['Action', 'Adventure', 'Animation', 'Comedy', 'Crime', 'Documentary', 'Drama', 'Fantasy', 'Horror', 'Mystery', 'Romance', 'Sci-Fi', 'Thriller', 'Western', 'Anime'].map(g => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </div>

                <div className="max-h-80 overflow-y-auto space-y-1">
                  {allContent
                    .filter(c => {
                      const matchesSearch = !featuredSearch || c.title?.toLowerCase().includes(featuredSearch.toLowerCase()) || c.creator?.toLowerCase().includes(featuredSearch.toLowerCase());
                      const matchesGenre = !featuredGenreFilter || c.genres?.includes(featuredGenreFilter);
                      return matchesSearch && matchesGenre;
                    })
                    .map(item => {
                      const isSelected = featuredIds.includes(item.film_id);
                      return (
                        <div
                          key={item.film_id}
                          className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                            isSelected
                              ? 'border-[#8B5CF6]/50 bg-[#8B5CF6]/10'
                              : 'border-white/5 bg-[#0a0a1f] hover:border-white/20'
                          }`}
                          onClick={() => {
                            if (isSelected) {
                              setFeaturedIds(featuredIds.filter(id => id !== item.film_id));
                            } else if (featuredIds.length < 5) {
                              setFeaturedIds([...featuredIds, item.film_id]);
                            } else {
                              toast.error('Maximum 5 featured items');
                            }
                          }}
                          data-testid={`content-item-${item.film_id}`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            isSelected ? 'border-[#8B5CF6] bg-[#8B5CF6]' : 'border-gray-600'
                          }`}>
                            {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                          </div>
                          <img src={item.thumbnail} alt={item.title} className="w-16 h-10 object-cover rounded flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{item.title}</p>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span className={item.content_type === 'series' ? 'text-[#00FF9D]' : 'text-[#8B5CF6]'}>
                                {item.content_type === 'series' ? 'Series' : 'Movie'}
                              </span>
                              <span>•</span>
                              <span>{item.creator}</span>
                              {item.year && <><span>•</span><span>{item.year}</span></>}
                            </div>
                          </div>
                          <div className="text-right text-xs text-gray-500 flex-shrink-0">
                            <p>{item.views?.toLocaleString()} views</p>
                            <p>{item.avg_rating?.toFixed(1)} ★</p>
                          </div>
                        </div>
                      );
                    })}
                  {allContent.filter(c => {
                    const matchesSearch = !featuredSearch || c.title?.toLowerCase().includes(featuredSearch.toLowerCase()) || c.creator?.toLowerCase().includes(featuredSearch.toLowerCase());
                    const matchesGenre = !featuredGenreFilter || c.genres?.includes(featuredGenreFilter);
                    return matchesSearch && matchesGenre;
                  }).length === 0 && (
                    <p className="text-center text-gray-500 py-8">No content found matching your search.</p>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Admins Tab - Owner Only */}
          {isOwnerRole && (
            <TabsContent value="admins">
              <div className="space-y-6">
                {/* Create Admin Form */}
                <div className="admin-card p-6" data-testid="create-admin-form">
                  <h3 className="text-lg font-bold mb-4">Create New Admin</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      placeholder="Admin name"
                      value={adminForm.name}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, name: e.target.value }))}
                      className="bg-[#030014] border-[#8B5CF6]/20"
                      data-testid="admin-name-input"
                    />
                    <Input
                      type="email"
                      placeholder="Email address"
                      value={adminForm.email}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, email: e.target.value }))}
                      className="bg-[#030014] border-[#8B5CF6]/20"
                      data-testid="admin-email-input"
                    />
                    <Input
                      type="password"
                      placeholder="Password (min 6 chars)"
                      value={adminForm.password}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, password: e.target.value }))}
                      className="bg-[#030014] border-[#8B5CF6]/20"
                      data-testid="admin-password-input"
                    />
                  </div>
                  <Button
                    onClick={handleCreateAdmin}
                    disabled={creatingAdmin}
                    className="mt-4 btn-primary"
                    data-testid="create-admin-button"
                  >
                    {creatingAdmin ? 'Creating...' : 'Create Admin'}
                  </Button>
                  <p className="text-xs text-gray-500 mt-2">
                    Admins can manage content and users but cannot access financial data (revenue, payouts, subscriptions).
                  </p>
                </div>

                {/* Admin List */}
                <div className="admin-card p-6" data-testid="admin-list">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold">Current Admins ({adminUsers.length})</h3>
                    {adminUsers.length > 0 && (
                      <div className="relative w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <Input
                          placeholder="Search admins..."
                          value={peopleSearch}
                          onChange={e => setPeopleSearch(e.target.value)}
                          className="pl-9 bg-[#030014] border-white/10 h-9 text-sm"
                          data-testid="admin-search-input"
                        />
                      </div>
                    )}
                  </div>
                  {adminUsers.length === 0 ? (
                    <p className="text-gray-400">No admins created yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {adminUsers.filter(a => {
                        if (!peopleSearch.trim()) return true;
                        const q = peopleSearch.toLowerCase();
                        return (a.name || '').toLowerCase().includes(q) || (a.email || '').toLowerCase().includes(q);
                      }).map((admin) => (
                        <div key={admin.user_id} className="flex items-center justify-between p-4 bg-[#030014] rounded-lg border border-[#8B5CF6]/10" data-testid={`admin-row-${admin.user_id}`}>
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center">
                              <User className="w-5 h-5 text-[#8B5CF6]" />
                            </div>
                            <div>
                              <p className="font-medium">{admin.name}</p>
                              <p className="text-sm text-gray-400">{admin.email}</p>
                            </div>
                            <span className="badge text-xs bg-[#00FF9D]/20 text-[#00FF9D] border-[#00FF9D]/30">Admin</span>
                          </div>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setDeleteAdminDialog({ open: true, user: admin })}
                            data-testid={`delete-admin-${admin.user_id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" /> Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          )}

          {/* Live Audience Tab */}
          <TabsContent value="live-audience">
            <LiveAudienceTab />
          </TabsContent>

          {/* Legal & Privacy Tab */}
          <TabsContent value="legal-privacy">
            <LegalPrivacyPanel />
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="agreements">
            <AgreementsPanel />
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <ReportsPanel />
          </TabsContent>
        </Tabs>
      </div>

      {/* Approve Dialog */}
      <Dialog open={approveDialog.open} onOpenChange={(open) => { if (!open) { setApproveDialog({ open: false, type: null, id: null, name: '' }); setApproveNote(''); } }}>
        <DialogContent className="bg-[#0F0B1E] border-[#00FF9D]/30">
          <DialogHeader>
            <DialogTitle className="text-[#00FF9D]">Approve {approveDialog.type === 'company' ? 'Studio' : 'Content'}</DialogTitle>
            <DialogDescription className="text-gray-400">
              Approving "{approveDialog.name}" - a notification email will be sent.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Note for the creator (optional)</label>
              <Textarea 
                value={approveNote} 
                onChange={(e) => setApproveNote(e.target.value)} 
                placeholder="Add any notes, tips, or welcome message for the creator..." 
                className="bg-[#030014] border-[#8B5CF6]/20 min-h-[80px]" 
              />
              <p className="text-xs text-gray-500 mt-1">This note will be included in the approval email.</p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setApproveDialog({ open: false, type: null, id: null, name: '' }); setApproveNote(''); }}>Cancel</Button>
              <Button onClick={approveDialog.type === 'company' ? handleApproveCompany : handleApproveContent} className="bg-[#00FF9D] hover:bg-[#00FF9D]/80 text-black">
                <CheckCircle className="w-4 h-4 mr-2" /> Approve
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => { if (!open) { setRejectDialog({ open: false, type: null, id: null }); setRejectReason(''); setRejectNote(''); } }}>
        <DialogContent className="bg-[#0F0B1E] border-red-500/30">
          <DialogHeader>
            <DialogTitle className="text-red-400">Reject {rejectDialog.type === 'company' ? 'Studio' : 'Content'}</DialogTitle>
            <DialogDescription className="text-gray-400">
              A notification email with the rejection reason will be sent.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Reason for Rejection <span className="text-red-400">*</span></label>
              <Textarea 
                value={rejectReason} 
                onChange={(e) => setRejectReason(e.target.value)} 
                placeholder="Provide a clear reason for rejection (required)..." 
                className="bg-[#030014] border-[#8B5CF6]/20 min-h-[80px]" 
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Additional Notes (optional)</label>
              <Textarea 
                value={rejectNote} 
                onChange={(e) => setRejectNote(e.target.value)} 
                placeholder="Add any additional feedback or suggestions..." 
                className="bg-[#030014] border-[#8B5CF6]/20 min-h-[60px]" 
              />
              <p className="text-xs text-gray-500 mt-1">Both the reason and notes will be included in the rejection email.</p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setRejectDialog({ open: false, type: null, id: null }); setRejectReason(''); setRejectNote(''); }}>Cancel</Button>
              <Button onClick={rejectDialog.type === 'company' ? handleRejectCompany : handleRejectContent} className="bg-red-500 hover:bg-red-600">
                <XCircle className="w-4 h-4 mr-2" /> Reject
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, type: null, id: null, name: '' })}>
        <DialogContent className="bg-[#0F0B1E] border-red-500/30">
          <DialogHeader>
            <DialogTitle className="text-red-400">Delete {deleteDialog.type?.charAt(0).toUpperCase() + deleteDialog.type?.slice(1)}</DialogTitle>
            <DialogDescription className="text-gray-400">
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-gray-300">
              Are you sure you want to delete <span className="font-semibold text-white">"{deleteDialog.name}"</span>?
              {deleteDialog.type === 'company' && (
                <span className="block text-sm text-red-400 mt-2">This will also delete all content created by this company.</span>
              )}
              {deleteDialog.type === 'series' && (
                <span className="block text-sm text-red-400 mt-2">This will also delete all episodes in this series.</span>
              )}
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setDeleteDialog({ open: false, type: null, id: null, name: '' })}>Cancel</Button>
              <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Episodes Management Dialog */}
      <Dialog open={episodesDialog.open} onOpenChange={(open) => { if (!open) { setEpisodesDialog({ open: false, seriesId: null, seriesName: '' }); setSelectedSeries(null); } }}>
        <DialogContent className="bg-[#0F0B1E] border-[#8B5CF6]/30 max-w-2xl">
          <DialogHeader>
            <DialogTitle>{episodesDialog.seriesName} - Episodes</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4 max-h-[60vh] overflow-y-auto">
            {seriesEpisodes.length > 0 ? (
              seriesEpisodes.map((ep) => (
                <div key={ep.episode_id} className="flex items-center justify-between p-3 admin-card" data-testid={`episode-row-${ep.episode_id}`}>
                  <div className="flex items-center gap-3">
                    {ep.thumbnail && <img src={ep.thumbnail} alt={ep.title} className="w-16 h-10 object-cover rounded" />}
                    <div>
                      <p className="font-medium text-sm">S{ep.season_number}E{ep.episode_number}: {ep.title}</p>
                      <p className="text-xs text-gray-500">{ep.description?.slice(0, 50)}...</p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => setDeleteDialog({ open: true, type: 'episode', id: ep.episode_id, name: `S${ep.season_number}E${ep.episode_number}: ${ep.title}` })} 
                    size="sm" 
                    variant="outline" 
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    data-testid={`delete-episode-${ep.episode_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-400 py-8">No episodes found</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Company Analytics Dialog */}
      <Dialog open={companyAnalyticsDialog.open} onOpenChange={(open) => !open && setCompanyAnalyticsDialog({ open: false, company: null, analytics: null })}>
        <DialogContent className="bg-[#0F0B1E] border-[#8B5CF6]/30 max-w-2xl">
          <DialogHeader>
            <DialogTitle>{companyAnalyticsDialog.company?.name} - Analytics</DialogTitle>
          </DialogHeader>
          {companyAnalyticsDialog.analytics && (
            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-4 gap-4">
                <div className="admin-card p-3 text-center">
                  <p className="text-xl font-bold">{companyAnalyticsDialog.analytics.total_films}</p>
                  <p className="text-xs text-gray-400">Films</p>
                </div>
                <div className="admin-card p-3 text-center">
                  <p className="text-xl font-bold">{companyAnalyticsDialog.analytics.total_views?.toLocaleString()}</p>
                  <p className="text-xs text-gray-400">Views</p>
                </div>
                <div className="admin-card p-3 text-center">
                  <p className="text-xl font-bold">{companyAnalyticsDialog.analytics.total_watch_hours}h</p>
                  <p className="text-xs text-gray-400">Watch Hours</p>
                </div>
                <div className="admin-card p-3 text-center">
                  <p className="text-xl font-bold">{companyAnalyticsDialog.analytics.average_rating}</p>
                  <p className="text-xs text-gray-400">Avg Rating</p>
                </div>
              </div>
              <h4 className="font-semibold">Content Breakdown</h4>
              <div className="max-h-60 overflow-y-auto">
                {companyAnalyticsDialog.analytics.film_stats?.map((film) => (
                  <div key={film.film_id} className="flex justify-between items-center py-2 border-b border-white/5">
                    <div>
                      <p className="font-medium text-sm">{film.title}</p>
                      {getStatusBadge(film.status)}
                    </div>
                    <div className="text-right text-sm">
                      <p>{film.views} views</p>
                      <p className="text-gray-400">{film.watch_hours}h watched</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Content Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, film: null })}>
        <DialogContent className="bg-[#0F0B1E] border-[#8B5CF6]/20 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Content</DialogTitle>
            <DialogDescription>Modify content details. Only owner can edit.</DialogDescription>
          </DialogHeader>
          {editDialog.film && (
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Title</label>
                <Input value={editForm.title} onChange={(e) => setEditForm(p => ({...p, title: e.target.value}))} className="bg-[#030014] border-[#8B5CF6]/20" data-testid="edit-title" />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Description</label>
                <Textarea value={editForm.description} onChange={(e) => setEditForm(p => ({...p, description: e.target.value}))} className="bg-[#030014] border-[#8B5CF6]/20 min-h-[80px]" data-testid="edit-description" />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Year</label>
                <Input type="number" value={editForm.year} onChange={(e) => setEditForm(p => ({...p, year: parseInt(e.target.value) || 2025}))} className="bg-[#030014] border-[#8B5CF6]/20 w-32" data-testid="edit-year" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">YouTube ID</label>
                  <Input value={editForm.youtube_id} onChange={(e) => setEditForm(p => ({...p, youtube_id: e.target.value.trim()}))} className="bg-[#030014] border-[#8B5CF6]/20" data-testid="edit-youtube" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Vimeo ID</label>
                  <Input value={editForm.vimeo_id} onChange={(e) => setEditForm(p => ({...p, vimeo_id: e.target.value.trim()}))} className="bg-[#030014] border-[#8B5CF6]/20" data-testid="edit-vimeo" />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Genres ({editForm.genres.length}/5)</label>
                <div className="flex flex-wrap gap-2">
                  {GENRE_OPTIONS.map((genre) => (
                    <button key={genre} type="button" onClick={() => toggleEditGenre(genre)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all border ${
                        editForm.genres.includes(genre) ? 'bg-[#8B5CF6]/30 text-[#8B5CF6] border-[#8B5CF6]/50' : 'border-white/10 text-gray-400 hover:border-white/20'
                      }`}
                      data-testid={`edit-genre-${genre.toLowerCase()}`}
                    >
                      {genre}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Age Rating</label>
                <div className="flex gap-2">
                  {['7+', '12+', '14+', '16+'].map((rating) => (
                    <button key={rating} type="button" onClick={() => setEditForm(p => ({...p, content_rating: rating}))}
                      className={`px-4 py-2 rounded-lg text-sm font-bold transition-all border ${
                        editForm.content_rating === rating ? 'bg-[#8B5CF6]/30 text-[#8B5CF6] border-[#8B5CF6]/50' : 'border-white/10 text-gray-400 hover:border-white/20'
                      }`}
                      data-testid={`edit-rating-${rating}`}
                    >
                      {rating}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button onClick={handleSaveEdit} disabled={editSaving || !editForm.title} className="btn-primary flex-1" data-testid="save-edit-btn">
                  {editSaving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline" onClick={() => setEditDialog({ open: false, film: null })} className="border-gray-700">Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Content Analytics Dialog */}
      <Dialog open={analyticsDialog.open} onOpenChange={(open) => !open && setAnalyticsDialog({ open: false, data: null, loading: false })}>
        <DialogContent className="bg-[#0F0B1E] border-[#8B5CF6]/20 max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detailedAnalytics?.title || 'Content'} Analytics</DialogTitle>
            <DialogDescription>Detailed performance metrics</DialogDescription>
          </DialogHeader>
          {analyticsDialog.loading ? (
            <div className="flex justify-center py-12"><div className="spinner" /></div>
          ) : detailedAnalytics && (
            <div className="space-y-6 mt-2">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <div className="admin-card p-3 text-center">
                  <Eye className="w-5 h-5 text-[#8B5CF6] mx-auto mb-1" />
                  <p className="text-xl font-bold">{detailedAnalytics.total_views?.toLocaleString()}</p>
                  <p className="text-xs text-gray-400">Views</p>
                </div>
                <div className="admin-card p-3 text-center">
                  <Clock className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                  <p className="text-xl font-bold">{detailedAnalytics.total_watch_hours}h</p>
                  <p className="text-xs text-gray-400">Watch Hours</p>
                </div>
                <div className="admin-card p-3 text-center">
                  <Users className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
                  <p className="text-xl font-bold">{detailedAnalytics.unique_viewers}</p>
                  <p className="text-xs text-gray-400">Unique Viewers</p>
                </div>
                <div className="admin-card p-3 text-center">
                  <TrendingUp className="w-5 h-5 text-[#00FF9D] mx-auto mb-1" />
                  <p className="text-xl font-bold">{detailedAnalytics.retention_rate}%</p>
                  <p className="text-xs text-gray-400">Retention</p>
                </div>
                <div className="admin-card p-3 text-center">
                  <Star className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
                  <p className="text-xl font-bold">{detailedAnalytics.avg_rating?.toFixed(1)}</p>
                  <p className="text-xs text-gray-400">{detailedAnalytics.rating_count} ratings</p>
                </div>
                <div className="admin-card p-3 text-center">
                  <DollarSign className="w-5 h-5 text-green-400 mx-auto mb-1" />
                  <p className="text-xl font-bold">{'\u0024'}{detailedAnalytics.total_donations}</p>
                  <p className="text-xs text-gray-400">{detailedAnalytics.donation_count} donations</p>
                </div>
              </div>

              {/* Engagement Score */}
              <div className="admin-card p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold">Engagement Score</h4>
                  <span className="text-2xl font-bold text-[#00FF9D]">{detailedAnalytics.engagement_score}/100</span>
                </div>
                <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-red-500 via-yellow-400 to-[#00FF9D]" style={{ width: `${detailedAnalytics.engagement_score}%` }} />
                </div>
                <p className="text-xs text-gray-500 mt-1">Based on retention, ratings, and donations</p>
              </div>

              {/* Views Graph - 30 days */}
              {detailedAnalytics.views_graph?.length > 0 && (
                <div className="admin-card p-4">
                  <h4 className="text-sm font-semibold mb-3">Views & Watch Hours (Last 30 Days)</h4>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={detailedAnalytics.views_graph}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1e1b4b" />
                        <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: '#0F0B1E', border: '1px solid #8B5CF6', borderRadius: '8px' }} />
                        <Area type="monotone" dataKey="views" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.2} name="Views" />
                        <Area type="monotone" dataKey="hours" stroke="#00FF9D" fill="#00FF9D" fillOpacity={0.1} name="Hours" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Rating Distribution */}
              <div className="admin-card p-4">
                <h4 className="text-sm font-semibold mb-3">Rating Distribution</h4>
                <div className="space-y-2">
                  {[5, 4, 3, 2, 1].map((star) => {
                    const count = detailedAnalytics.rating_distribution?.[star] || 0;
                    const total = detailedAnalytics.rating_count || 1;
                    const pct = Math.round((count / total) * 100) || 0;
                    return (
                      <div key={star} className="flex items-center gap-3">
                        <span className="text-sm w-6 text-right">{star}</span>
                        <Star className="w-3 h-3 text-yellow-400" />
                        <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                          <div className="h-full bg-yellow-400 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-gray-400 w-12 text-right">{count} ({pct}%)</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Avg Watch Time */}
              <div className="admin-card p-4">
                <h4 className="text-sm font-semibold mb-3">Engagement Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-400">Avg Watch Time:</span> <span className="font-medium">{detailedAnalytics.avg_watch_minutes} min</span></div>
                  <div><span className="text-gray-400">Retention Rate:</span> <span className="font-medium">{detailedAnalytics.retention_rate}%</span></div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Admin Dialog */}
      <Dialog open={deleteAdminDialog.open} onOpenChange={(open) => { if (!open) setDeleteAdminDialog({ open: false, user: null }); }}>
        <DialogContent className="bg-[#0F0B1E] border-red-500/30">
          <DialogHeader>
            <DialogTitle className="text-red-400">Remove Admin</DialogTitle>
            <DialogDescription className="text-gray-400">
              Are you sure you want to remove "{deleteAdminDialog.user?.name}" ({deleteAdminDialog.user?.email}) as an admin? This will delete their account and revoke all access.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-4">
            <Button variant="outline" className="flex-1" onClick={() => setDeleteAdminDialog({ open: false, user: null })} data-testid="cancel-delete-admin">
              Cancel
            </Button>
            <Button variant="destructive" className="flex-1" onClick={handleDeleteAdmin} data-testid="confirm-delete-admin">
              Remove Admin
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Content Preview Dialog */}
      <Dialog open={previewDialog.open} onOpenChange={(open) => { if (!open) setPreviewDialog({ open: false, item: null, episodes: null, selectedEp: null }); }}>
        <DialogContent className="bg-[#0F0B1E] border-[#8B5CF6]/30 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-5 h-5 text-[#8B5CF6]" />
              Preview: {previewDialog.item?.title}
              {previewDialog.item?.content_type === 'series' && (
                <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-[#00FF9D]/20 text-[#00FF9D]">Series</span>
              )}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Review content before approving for publication
            </DialogDescription>
          </DialogHeader>

          {/* Video Player */}
          <div className="mt-4">
            {getPreviewVideoData()?.video_file ? (
              <VideoPlayer
                key={previewDialog.selectedEp?.episode_id || previewDialog.item?.film_id}
                film={getPreviewVideoData()}
                compact
              />
            ) : (
              <div className="w-full aspect-video bg-black/50 rounded-lg flex items-center justify-center">
                <p className="text-gray-500">No video file uploaded</p>
              </div>
            )}
          </div>

          {/* Episode selector for series */}
          {previewDialog.item?.content_type === 'series' && previewDialog.episodes?.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-300 mb-2">Episodes ({previewDialog.episodes.length})</p>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {previewDialog.episodes.map((ep) => (
                  <button
                    key={ep.episode_id}
                    onClick={() => setPreviewDialog(prev => ({ ...prev, selectedEp: ep }))}
                    className={`w-full text-left p-2.5 rounded-lg text-sm flex items-center gap-3 transition-colors ${ep.episode_id === previewDialog.selectedEp?.episode_id ? 'bg-[#8B5CF6]/20 border border-[#8B5CF6]/40' : 'hover:bg-white/5 border border-transparent'}`}
                    data-testid={`preview-episode-${ep.episode_id}`}
                  >
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${ep.episode_id === previewDialog.selectedEp?.episode_id ? 'bg-[#8B5CF6] text-white' : 'bg-white/10 text-gray-400'}`}>
                      {ep.episode_number}
                    </span>
                    <span>S{ep.season_number}E{ep.episode_number}: {ep.title}</span>
                    <span className="ml-auto text-xs text-gray-500">{ep.video_file ? 'Has video' : 'No video'}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Content Details */}
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div className="admin-card p-3">
              <p className="text-xs text-gray-500">Creator</p>
              <p className="font-medium">{previewDialog.item?.creator}</p>
            </div>
            <div className="admin-card p-3">
              <p className="text-xs text-gray-500">Genres</p>
              <p className="font-medium">{previewDialog.item?.genres?.join(', ') || 'None'}</p>
            </div>
          </div>
          {previewDialog.item?.description && (
            <div className="admin-card p-3 mt-2">
              <p className="text-xs text-gray-500 mb-1">Description</p>
              <p className="text-sm text-gray-300">{previewDialog.item.description}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Ban User Dialog */}
      <Dialog open={banDialog.open} onOpenChange={(open) => { if (!open) { setBanDialog({ open: false, user: null }); setBanReason(''); } }}>
        <DialogContent className="bg-[#0F0B1E] border-red-500/30">
          <DialogHeader>
            <DialogTitle className="text-red-400">Ban User</DialogTitle>
            <DialogDescription className="text-gray-400">
              Ban "{banDialog.user?.name}" ({banDialog.user?.email})? They will be logged out and unable to access the platform.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Input
              placeholder="Reason for ban (optional)"
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              className="bg-[#030014] border-red-500/20"
              data-testid="ban-reason-input"
            />
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => { setBanDialog({ open: false, user: null }); setBanReason(''); }} data-testid="cancel-ban">
                Cancel
              </Button>
              <Button variant="destructive" className="flex-1" onClick={handleBanUser} data-testid="confirm-ban">
                Ban User
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Policy History Dialog */}
      <Dialog open={policyHistoryDialog.open} onOpenChange={(open) => { if (!open) setPolicyHistoryDialog({ open: false, userId: null, userName: '' }); }}>
        <DialogContent className="bg-[#0F0B1E] border-white/10 max-w-lg max-h-[80vh] p-0" data-testid="policy-history-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/10">
            <DialogTitle>Policy Consent History</DialogTitle>
            <DialogDescription className="text-gray-400">{policyHistoryDialog.userName}</DialogDescription>
          </DialogHeader>
          <div className="px-6 py-4 overflow-y-auto" style={{ maxHeight: '55vh' }}>
            {policyConsents.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">No policy consents recorded</p>
            ) : (
              <div className="space-y-3">
                {policyConsents.map((c, i) => (
                  <div key={i} className="p-3 bg-[#030014] border border-white/5 rounded-lg" data-testid={`consent-record-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${c.context === 'registration' ? 'bg-[#00FF9D]/20 text-[#00FF9D]' : 'bg-amber-500/20 text-amber-400'}`}>
                        {c.context === 'registration' ? 'Registration' : 'Reconfirmation'}
                      </span>
                      <span className="text-xs text-gray-500">{new Date(c.timestamp).toLocaleString('en-US')}</span>
                    </div>
                    <div className="text-xs text-gray-400 space-y-1">
                      <p><span className="text-gray-500">IP:</span> {c.ip_address}</p>
                      {c.policies_accepted?.map((p, j) => (
                        <p key={j}><span className="text-gray-500">Policy:</span> {p.slug} <span className="text-gray-600">v{p.version}</span></p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Request Reason Dialog */}
      {editReqAction && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => { setEditReqAction(null); setEditReqReason(''); }}>
          <div className="bg-[#0F0B1E] border border-white/10 rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="edit-req-reason-dialog">
            <h3 className="text-lg font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {editReqAction.action === 'approve' ? 'Approve' : 'Reject'} Edit Request
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              {editReqAction.action === 'approve' ? 'Approving' : 'Rejecting'} "{editReqAction.name}". Please provide a reason (required).
            </p>
            <textarea
              value={editReqReason}
              onChange={e => setEditReqReason(e.target.value)}
              placeholder="Enter reason (required)"
              className="w-full h-24 bg-white/5 border border-white/10 rounded-lg p-3 text-sm text-white placeholder-gray-500 resize-none focus:outline-none focus:border-[#8B5CF6]/50"
              data-testid="edit-req-reason-input"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => { setEditReqAction(null); setEditReqReason(''); }} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors">Cancel</button>
              <button
                onClick={async () => {
                  if (!editReqReason.trim()) { toast.error('Reason is required'); return; }
                  try {
                    if (editReqAction.action === 'approve') {
                      await editRequestsApi.approve(editReqAction.id, editReqReason.trim());
                      toast.success('Edit approved and applied');
                    } else {
                      await editRequestsApi.reject(editReqAction.id, editReqReason.trim());
                      toast.success('Edit rejected');
                    }
                    setEditRequests(prev => prev.map(r => r.request_id === editReqAction.id ? {...r, status: editReqAction.action === 'approve' ? 'approved' : 'rejected', review_reason: editReqReason.trim()} : r));
                    setEditReqAction(null);
                    setEditReqReason('');
                  } catch (e) { toast.error(e.message || 'Failed'); }
                }}
                disabled={!editReqReason.trim()}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${editReqAction.action === 'approve' ? 'bg-[#00FF9D]/20 text-[#00FF9D] hover:bg-[#00FF9D]/30 border border-[#00FF9D]/30' : 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30'}`}
                data-testid="edit-req-submit-btn"
              >
                {editReqAction.action === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Creator Agreements Dialog */}
      <Dialog open={agreementsDialog.open} onOpenChange={(open) => { if (!open) { setAgreementsDialog({ open: false, companyId: null, companyName: '' }); setAgreementsData(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col bg-[#0F0B1E] border-white/10 p-0" data-testid="agreements-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/10 shrink-0">
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#00FF9D]" /> Agreements — {agreementsDialog.companyName}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-400 mt-1">Full history of accepted agreements and content submissions.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4" style={{ maxHeight: '60vh' }}>
            {agreementsLoading ? (
              <div className="flex justify-center py-12"><div className="spinner" /></div>
            ) : !agreementsData ? (
              <p className="text-gray-400 text-center py-8">No data available.</p>
            ) : (
              <div className="space-y-6">
                {/* Agreement Acceptances */}
                <div>
                  <h3 className="text-sm font-semibold text-[#00FF9D] mb-3 uppercase tracking-wider">Agreement Acceptances</h3>
                  {agreementsData.acceptances?.length > 0 ? (
                    <div className="space-y-2">
                      {agreementsData.acceptances.map((a) => (
                        <div key={a.acceptance_id} className="bg-[#030014] border border-white/5 rounded-lg p-3" data-testid={`acceptance-${a.acceptance_id}`}>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                            <div><span className="text-gray-500">User ID</span><p className="text-white font-mono mt-0.5">{a.user_id}</p></div>
                            <div><span className="text-gray-500">IP Address</span><p className="text-white font-mono mt-0.5">{a.ip_address}</p></div>
                            <div><span className="text-gray-500">Agreement Version</span><p className="text-white mt-0.5">v{a.agreement_version}</p></div>
                            <div><span className="text-gray-500">Accepted At</span><p className="text-white mt-0.5">{new Date(a.accepted_at).toLocaleString('en-US')}</p></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No agreements accepted yet.</p>
                  )}
                </div>

                {/* Content Submission Audit Trail */}
                <div>
                  <h3 className="text-sm font-semibold text-[#8B5CF6] mb-3 uppercase tracking-wider">Content Submission Audit Trail</h3>
                  {agreementsData.audit_trail?.length > 0 ? (
                    <div className="space-y-2">
                      {agreementsData.audit_trail.map((entry) => (
                        <div key={entry.audit_id} className="bg-[#030014] border border-white/5 rounded-lg p-3" data-testid={`audit-${entry.audit_id}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-[#8B5CF6]/20 text-[#8B5CF6]">{entry.content_type}</span>
                            <span className="text-sm font-medium text-white">{entry.content_title}</span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                            <div><span className="text-gray-500">User ID</span><p className="text-white font-mono mt-0.5">{entry.user_id}</p></div>
                            <div><span className="text-gray-500">IP Address</span><p className="text-white font-mono mt-0.5">{entry.ip_address}</p></div>
                            <div><span className="text-gray-500">Agreement v.</span><p className="text-white mt-0.5">v{entry.agreement_version}</p></div>
                            <div><span className="text-gray-500">Submitted At</span><p className="text-white mt-0.5">{new Date(entry.timestamp).toLocaleString('en-US')}</p></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No content submissions recorded.</p>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="px-6 py-4 border-t border-white/10 flex justify-end shrink-0">
            <Button onClick={() => handleDownloadAgreements(agreementsDialog.companyId)} variant="outline" className="border-[#00FF9D]/30 text-[#00FF9D]" data-testid="download-agreements-btn">
              <Download className="w-4 h-4 mr-2" /> Download Records
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ReportsPanel = () => {
  const [reports, setReports] = React.useState([]);
  const [counts, setCounts] = React.useState({ pending: 0, resolved: 0, total: 0 });
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState('pending');
  const [resolveNote, setResolveNote] = React.useState('');
  const [resolvingId, setResolvingId] = React.useState(null);
  const [activeReportAction, setActiveReportAction] = React.useState(null);

  const fetchReports = React.useCallback(async () => {
    try {
      setLoading(true);
      const data = await reportsApi.getAll(filter);
      setReports(data.reports || []);
      setCounts(data.counts || { pending: 0, resolved: 0, total: 0 });
    } catch (e) {
      console.error('Failed to load reports:', e);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  React.useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleResolve = async (reportId, action) => {
    if ((action === 'pause' || action === 'remove') && !resolveNote.trim()) {
      toast.error('You must provide a reason for pausing or removing content');
      return;
    }
    try {
      setResolvingId(reportId);
      await reportsApi.resolve(reportId, action, resolveNote);
      toast.success(`Report ${action === 'dismiss' ? 'dismissed' : action === 'pause' ? 'content paused' : 'content removed'}`);
      setResolveNote('');
      setActiveReportAction(null);
      fetchReports();
    } catch (e) {
      toast.error(e.message || 'Failed to resolve report');
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-panel">
      <div className="admin-card p-4 border-red-500/20 bg-red-500/5">
        <div className="flex items-center gap-3">
          <Flag className="w-5 h-5 text-red-400" />
          <div>
            <p className="font-medium">Content Reports</p>
            <p className="text-sm text-gray-400">Review and manage user-reported content. You can dismiss reports or remove offending content.</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="admin-card p-4 text-center">
          <p className="text-2xl font-bold text-red-400">{counts.pending}</p>
          <p className="text-xs text-gray-400 mt-1">Pending</p>
        </div>
        <div className="admin-card p-4 text-center">
          <p className="text-2xl font-bold text-green-400">{counts.resolved}</p>
          <p className="text-xs text-gray-400 mt-1">Resolved</p>
        </div>
        <div className="admin-card p-4 text-center">
          <p className="text-2xl font-bold text-gray-300">{counts.total}</p>
          <p className="text-xs text-gray-400 mt-1">Total</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {['pending', 'all'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === f ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
            data-testid={`report-filter-${f}`}
          >
            {f === 'pending' ? 'Pending' : 'All Reports'}
          </button>
        ))}
      </div>

      {/* Reports list */}
      {loading ? (
        <div className="flex justify-center py-8"><div className="spinner" /></div>
      ) : reports.length > 0 ? (
        <div className="space-y-3">
          {reports.map(report => (
            <div key={report.report_id} className="admin-card p-4" data-testid={`report-${report.report_id}`}>
              <div className="flex items-start gap-4">
                {report.film_thumbnail && (
                  <img src={report.film_thumbnail} alt="" className="w-20 h-12 object-cover rounded-md shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-sm truncate">{report.film_title}</p>
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${report.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : report.status === 'dismissed' ? 'bg-gray-500/20 text-gray-400' : 'bg-red-500/20 text-red-400'}`}>
                      {report.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-300 mb-2">"{report.reason}"</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>Reported by: {report.user_name || report.user_email}</span>
                    <span>Creator: {report.creator}</span>
                    <span>{new Date(report.created_at).toLocaleDateString('en-US')}</span>
                  </div>

                  {report.status === 'pending' && (
                    <div className="mt-3 space-y-3">
                      {activeReportAction?.reportId === report.report_id ? (
                        <div className="space-y-2 p-3 rounded-lg border border-white/10 bg-[#030014]">
                          <p className="text-xs font-medium text-gray-300">
                            {activeReportAction.action === 'pause' ? 'Reason for pausing content:' : 'Reason for removing content:'}
                            <span className="text-red-400 ml-1">*</span>
                          </p>
                          <textarea
                            value={resolveNote}
                            onChange={e => setResolveNote(e.target.value)}
                            placeholder="Explain to the creator why this content is being actioned..."
                            className="w-full bg-[#0F0B1E] border border-white/10 rounded-lg p-2.5 text-sm text-gray-300 placeholder-gray-600 min-h-[80px] focus:border-[#8B5CF6]/50 focus:outline-none"
                            data-testid={`resolve-reason-${report.report_id}`}
                          />
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => { setActiveReportAction(null); setResolveNote(''); }}
                              className="px-3 py-1.5 text-xs font-medium bg-gray-500/20 text-gray-300 hover:bg-gray-500/30 rounded-lg transition-colors"
                              data-testid={`cancel-action-${report.report_id}`}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleResolve(report.report_id, activeReportAction.action)}
                              disabled={resolvingId === report.report_id || !resolveNote.trim()}
                              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 ${
                                activeReportAction.action === 'remove'
                                  ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30'
                                  : 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 border border-yellow-500/30'
                              }`}
                              data-testid={`confirm-action-${report.report_id}`}
                            >
                              {resolvingId === report.report_id ? 'Processing...' : `Confirm ${activeReportAction.action === 'pause' ? 'Pause' : 'Remove'}`}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleResolve(report.report_id, 'dismiss')}
                            disabled={resolvingId === report.report_id}
                            className="px-3 py-1.5 text-xs font-medium bg-gray-500/20 text-gray-300 hover:bg-gray-500/30 rounded-lg transition-colors disabled:opacity-50"
                            data-testid={`dismiss-report-${report.report_id}`}
                          >
                            Dismiss
                          </button>
                          <button
                            onClick={() => { setActiveReportAction({ reportId: report.report_id, action: 'pause' }); setResolveNote(''); }}
                            disabled={resolvingId === report.report_id}
                            className="px-3 py-1.5 text-xs font-medium bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 border border-yellow-500/30 rounded-lg transition-colors disabled:opacity-50"
                            data-testid={`pause-report-${report.report_id}`}
                          >
                            Pause Content
                          </button>
                          <button
                            onClick={() => { setActiveReportAction({ reportId: report.report_id, action: 'remove' }); setResolveNote(''); }}
                            disabled={resolvingId === report.report_id}
                            className="px-3 py-1.5 text-xs font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30 rounded-lg transition-colors disabled:opacity-50"
                            data-testid={`remove-report-${report.report_id}`}
                          >
                            Remove Content
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {report.resolved_at && (
                    <p className="text-xs text-gray-500 mt-2">Resolved by {report.resolved_by} on {new Date(report.resolved_at).toLocaleDateString('en-US')}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Flag className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">No {filter === 'pending' ? 'pending ' : ''}reports</p>
        </div>
      )}
    </div>
  );
};

export default OwnerDashboardPage;
